/**
 * LobbyController - Lobby Form Synchronization, Brand Visibility & Roster Presentation
 * Encapsulates brand switching rules, CPU availability constraints, player count UI sync,
 * settings store deserialization, and multiplayer lobby roster generation.
 */

import { ReactionManager } from './ReactionManager.js';
import { SeriesTracker } from '../series/SeriesTracker.js';
import { Board3DCatalog } from '../board/Board3DCatalog.js';
import { CRAZY_OPTIONS_DEFS, getVariantRequiredPlayers, areVariantPlayerRequirementsCompatible, isPlayerCountAllowedForVariant, CrazyManager } from '../variants/CrazyManager.js';
import { CrazySettings } from '../variants/CrazySettings.js';

export class LobbyController {
  /**
   * Applies visibility states, headers, and option rules for the specified brand.
   * @param {string} brand
   * @param {Object} [context]
   */
  static applyBrandVisibility(brand, context = {}) {
    if (typeof document === 'undefined') return;

    brand = brand || (typeof context.getAppBrand === 'function' ? context.getAppBrand() : 'crazy');
    const isClash = brand === 'clash';
    const isPints = brand === 'pints';
    const isAdult = brand === 'adult';

    const gameTitleEl = document.getElementById('gameHeaderTitle') || document.querySelector('.header-title-wrap h1');
    if (gameTitleEl) {
      gameTitleEl.textContent = isClash
        ? 'CRIBBAGE CLASH'
        : (isPints ? 'PINTS AND PEGS' : (isAdult ? 'SKUNKED AND STRIPPED' : 'CRAZY CRIBBAGE'));
    }

    const clashOptions = document.getElementById('cribbage-clash-options');
    const pintsOptions = document.getElementById('pints-options');
    const adultOptions = document.getElementById('adult-options');
    const crazyOptions = document.querySelectorAll('.crazy-only-options');
    const clashOnlyEls = document.querySelectorAll('.clash-only-options');
    const pintsOnlyEls = document.querySelectorAll('.pints-only-options');
    const adultOnlyEls = document.querySelectorAll('.adult-only-options');
    const checkAllWrap = document.querySelector('.checkAll');

    // Filter target options
    LobbyController.updateTargetOptions(brand, context);
    if (isClash) {
      if (context.settings && (!context.settings.target || ![241, 361, 481].includes(context.settings.target))) {
        context.settings.target = 481;
      }
      if (typeof window !== 'undefined' && window.settings && (!window.settings.target || ![241, 361, 481].includes(window.settings.target))) {
        window.settings.target = 481;
      }
    }

    // Filter 3D board options for active brand
    const el3dOpt = document.getElementById('opt3DBoard');
    if (el3dOpt) {
      const cur3dId = context.settings?.selected3DBoardId || (typeof window !== 'undefined' && window.settings?.selected3DBoardId) || el3dOpt.value;
      Board3DCatalog.syncDropdownOptions(el3dOpt, cur3dId, brand);
      if (context.settings) context.settings.selected3DBoardId = el3dOpt.value;
      if (typeof window !== 'undefined' && window.settings) window.settings.selected3DBoardId = el3dOpt.value;
    }

    const brandContainer = document.getElementById('brand-game-options');
    const GameOptionsEngine = context.GameOptionsEngine || (typeof window !== 'undefined' ? window.GameOptionsEngine : null);
    if (brandContainer && GameOptionsEngine) {
      if (brandContainer.getAttribute('data-rendered-brand') !== brand) {
        GameOptionsEngine.buildBrandSettingsUI(brand, brandContainer);
        brandContainer.setAttribute('data-rendered-brand', brand);
        const saved = GameOptionsEngine.loadBrandSettings(brand);
        if (saved) {
          GameOptionsEngine.applyBrandSettings(brand, saved);
        }
      }
    }
    LobbyController.disableOtherGameBonusOptions(brand, context.settings || (typeof window !== 'undefined' ? window.settings : {}), context);

    if (isClash) {
      if (clashOptions) clashOptions.style.display = 'block';
      clashOnlyEls.forEach(el => { el.style.display = el.classList.contains('opt') ? 'flex' : 'block'; });
      if (pintsOptions) {
        pintsOptions.style.display = 'none';
        if (typeof context.uncheckPintsOptions === 'function') context.uncheckPintsOptions();
      }
      pintsOnlyEls.forEach(el => {
        el.style.display = 'none';
        el.querySelectorAll('input[type="checkbox"]').forEach(chk => { chk.checked = false; });
      });
      if (adultOptions) {
        adultOptions.style.display = 'none';
        if (typeof context.uncheckAdultOptions === 'function') context.uncheckAdultOptions();
      }
      adultOnlyEls.forEach(el => {
        el.style.display = 'none';
        el.querySelectorAll('input[type="checkbox"]').forEach(chk => { chk.checked = false; });
      });
      if (typeof context.updateCribbageClashOptionRanges === 'function') {
        context.updateCribbageClashOptionRanges(true);
      }

      crazyOptions.forEach(el => {
        el.style.display = 'none';
        el.querySelectorAll('input[type="checkbox"]').forEach(chk => { chk.checked = false; });
      });

      if (checkAllWrap) {
        checkAllWrap.style.display = 'none';
        const checkAllChk = checkAllWrap.querySelector('input[type="checkbox"]');
        if (checkAllChk) checkAllChk.checked = false;
      }
    } else if (isPints) {
      if (clashOptions) {
        clashOptions.style.display = 'none';
        if (typeof context.uncheckCribbageClashOptions === 'function') context.uncheckCribbageClashOptions();
      }
      clashOnlyEls.forEach(el => {
        el.style.display = 'none';
        el.querySelectorAll('input[type="checkbox"]').forEach(chk => { chk.checked = false; });
      });
      if (pintsOptions) pintsOptions.style.display = 'block';
      pintsOnlyEls.forEach(el => { el.style.display = 'block'; });
      if (adultOptions) {
        adultOptions.style.display = 'none';
        if (typeof context.uncheckAdultOptions === 'function') context.uncheckAdultOptions();
      }
      adultOnlyEls.forEach(el => {
        el.style.display = 'none';
        el.querySelectorAll('input[type="checkbox"]').forEach(chk => { chk.checked = false; });
      });

      crazyOptions.forEach(el => { el.style.display = 'block'; });
      if (checkAllWrap) checkAllWrap.style.display = 'flex';
    } else if (isAdult) {
      if (clashOptions) {
        clashOptions.style.display = 'none';
        if (typeof context.uncheckCribbageClashOptions === 'function') context.uncheckCribbageClashOptions();
      }
      clashOnlyEls.forEach(el => {
        el.style.display = 'none';
        el.querySelectorAll('input[type="checkbox"]').forEach(chk => { chk.checked = false; });
      });
      if (pintsOptions) {
        pintsOptions.style.display = 'none';
        if (typeof context.uncheckPintsOptions === 'function') context.uncheckPintsOptions();
      }
      pintsOnlyEls.forEach(el => {
        el.style.display = 'none';
        el.querySelectorAll('input[type="checkbox"]').forEach(chk => { chk.checked = false; });
      });
      if (adultOptions) adultOptions.style.display = 'block';
      adultOnlyEls.forEach(el => { el.style.display = 'block'; });

      crazyOptions.forEach(el => { el.style.display = 'block'; });
      if (checkAllWrap) checkAllWrap.style.display = 'flex';
    } else {
      // Crazy Cribbage standard
      if (clashOptions) {
        clashOptions.style.display = 'none';
        if (typeof context.uncheckCribbageClashOptions === 'function') context.uncheckCribbageClashOptions();
      }
      clashOnlyEls.forEach(el => {
        el.style.display = 'none';
        el.querySelectorAll('input[type="checkbox"]').forEach(chk => { chk.checked = false; });
      });
      if (pintsOptions) {
        pintsOptions.style.display = 'none';
        if (typeof context.uncheckPintsOptions === 'function') context.uncheckPintsOptions();
      }
      pintsOnlyEls.forEach(el => {
        el.style.display = 'none';
        el.querySelectorAll('input[type="checkbox"]').forEach(chk => { chk.checked = false; });
      });
      if (adultOptions) {
        adultOptions.style.display = 'none';
        if (typeof context.uncheckAdultOptions === 'function') context.uncheckAdultOptions();
      }
      adultOnlyEls.forEach(el => {
        el.style.display = 'none';
        el.querySelectorAll('input[type="checkbox"]').forEach(chk => { chk.checked = false; });
      });

      crazyOptions.forEach(el => { el.style.display = 'block'; });
      if (checkAllWrap) checkAllWrap.style.display = 'flex';
    }

    const isCrazy = brand === 'crazy';
    const btnTourneyLaunch = document.getElementById('btnLaunchTourney');
    if (btnTourneyLaunch) btnTourneyLaunch.style.display = isCrazy ? 'inline-flex' : 'none';
    const btnModeTourney = document.getElementById('btnModeTourney');
    if (btnModeTourney) btnModeTourney.style.display = isCrazy ? 'inline-flex' : 'none';

    if (typeof context.updateCheckAllBonusesState === 'function') {
      context.updateCheckAllBonusesState();
    }

    const buildBoardFn = context.buildBoard || (typeof window !== 'undefined' ? (window.buildBoard || (window.crazyCribbageApp && window.crazyCribbageApp.buildBoard)) : null);
    if (typeof buildBoardFn === 'function') {
      try { buildBoardFn(); } catch (e) {}
    }
  }

  /**
   * Updates available target score options for the specified brand.
   * @param {string} brand
   * @param {Object} [context]
   */
  static updateTargetOptions(brand, context = {}) {
    if (typeof document === 'undefined') return;
    const optTarget = document.getElementById('optTarget');
    if (!optTarget) return;
    const isClash = brand === 'clash';
    const isCrazy = !brand || brand === 'crazy';
    const targetOptions = isClash
      ? [
          { val: '481', text: '481 points (Standard Match)' },
          { val: '361', text: '361 points (Short Match)' },
          { val: '241', text: '241 points (Mini Match)' }
        ]
      : (isCrazy
          ? [
              { val: '121', text: '121 points (Full game)' },
              { val: '61', text: '61 points (Short game)' },
              { val: '31', text: '31 points (Pegging Only)' },
              { val: '241', text: '241 points (There and Back)' }
            ]
          : [
              { val: '121', text: '121 points (Full game)' },
              { val: '61', text: '61 points (Short game)' },
              { val: '31', text: '31 points (Pegging Only)' }
            ]);

    const currentVal = optTarget.value;
    optTarget.innerHTML = '';
    targetOptions.forEach(opt => {
      const el = document.createElement('option');
      el.value = opt.val;
      el.textContent = opt.text;
      optTarget.appendChild(el);
    });

    const hasMatch = targetOptions.some(o => o.val === currentVal);
    const resolvedVal = hasMatch ? currentVal : (isClash ? '481' : '121');
    optTarget.value = resolvedVal;
    const targetInt = parseInt(resolvedVal, 10);
    if (context && context.settings) {
      context.settings.target = targetInt;
    }
    if (typeof window !== 'undefined' && window.settings) {
      window.settings.target = targetInt;
    }
  }

  /**
   * Synchronizes variant checkboxes and the Player Count dropdown according to variant player constraints.
   * If any variant requiring a specific player count is selected (checked):
   * 1. Variants requiring a different player count or conflicting mode are disabled.
   * 2. Dropdown options in #optPlayerCount are restricted to the required player count, and its value updated.
   * If no player-restricted variant is selected:
   * 1. All variant checkboxes are re-enabled.
   * 2. All player count options in #optPlayerCount are unlocked and visible.
   *
   * @param {Object} [context]
   * @returns {Object} { activeRestrictedVariant, allowedCountVal }
   */
  static syncVariantPlayerCountConstraints(context = {}) {
    if (typeof document === 'undefined') return { activeRestrictedVariant: null, allowedCountVal: null };

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const parseGameMode = context.parseGameMode || (typeof window !== 'undefined' ? window.parseGameMode : (v => ({ playerCount: parseInt(v, 10) || 2, gameMode: 'individual' })));
    const pCountEl = document.getElementById('optPlayerCount');

    // Find all restricted variant definitions
    const restrictedDefs = CRAZY_OPTIONS_DEFS.filter(opt => opt.requiredPlayers != null);

    // Identify which restricted variants are currently checked in the UI (or in settings)
    const activeRestrictedDefs = restrictedDefs.filter(opt => {
      const chk = document.getElementById(opt.chkId || opt.key);
      if (chk) return chk.checked;
      return Boolean(settings && (settings[opt.key] || settings[opt.chkId]));
    });

    let activeVariant = null;
    if (activeRestrictedDefs.length > 0) {
      if (context.changedEl) {
        const changedId = context.changedEl.id;
        const matched = activeRestrictedDefs.find(o => o.chkId === changedId || o.key === changedId);
        if (matched) activeVariant = matched;
      }
      if (!activeVariant) {
        activeVariant = activeRestrictedDefs[0];
      }

      // Uncheck any active variants that are incompatible with the chosen activeVariant
      activeRestrictedDefs.forEach(otherOpt => {
        if (otherOpt === activeVariant) return;
        if (!CrazyManager.areVariantPlayerRequirementsCompatible(activeVariant, otherOpt)) {
          const otherChk = document.getElementById(otherOpt.chkId || otherOpt.key);
          if (otherChk) otherChk.checked = false;
          if (settings) {
            settings[otherOpt.key] = false;
            if (otherOpt.chkId) settings[otherOpt.chkId] = false;
          }
        }
      });
    }

    // 1. Synchronize #optPlayerCount options
    if (pCountEl) {
      if (activeVariant) {
        if (settings) {
          settings[activeVariant.key] = true;
          if (activeVariant.chkId) settings[activeVariant.chkId] = true;
        }

        let firstAllowedVal = null;
        let isCurrentValAllowed = false;

        if (pCountEl.options && pCountEl.options.length > 0) {
          Array.from(pCountEl.options).forEach(opt => {
            const { playerCount, gameMode } = parseGameMode(opt.value);
            const allowed = CrazyManager.isPlayerCountAllowedForVariant(activeVariant, playerCount, gameMode, opt.value);
            opt.disabled = !allowed;
            opt.hidden = !allowed;
            opt.style.display = allowed ? '' : 'none';

            if (allowed) {
              if (!firstAllowedVal) firstAllowedVal = opt.value;
              if (activeVariant.requiredPlayerMode && opt.value === activeVariant.requiredPlayerMode) {
                firstAllowedVal = opt.value;
              }
              if (opt.value === pCountEl.value) {
                isCurrentValAllowed = true;
              }
            }
          });
        } else {
          firstAllowedVal = activeVariant.requiredPlayerMode || (activeVariant.requiredPlayers ? String(activeVariant.requiredPlayers) : '2');
          const { playerCount, gameMode } = parseGameMode(pCountEl.value);
          isCurrentValAllowed = CrazyManager.isPlayerCountAllowedForVariant(activeVariant, playerCount, gameMode, pCountEl.value);
        }

        if (!isCurrentValAllowed && firstAllowedVal) {
          pCountEl.value = firstAllowedVal;
        }
        const resolvedVal = pCountEl.value || firstAllowedVal;
        const parsed = parseGameMode(resolvedVal);
        if (settings) {
          settings.playerCount = parsed.playerCount;
          settings.gameMode = parsed.gameMode;
        }
      } else {
        // No restricted variant is active: unlock all dropdown options
        if (pCountEl.options) {
          Array.from(pCountEl.options).forEach(opt => {
            opt.disabled = false;
            opt.hidden = false;
            opt.style.display = '';
          });
        }
      }
    }

    // 2. Synchronize variant checkboxes based on activeVariant constraint
    restrictedDefs.forEach(opt => {
      const chk = document.getElementById(opt.chkId || opt.key);
      if (!chk) return;
      const label = chk.closest('.chk');

      if (activeVariant) {
        const isCompatible = CrazyManager.areVariantPlayerRequirementsCompatible(activeVariant, opt);
        if (isCompatible) {
          chk.disabled = false;
          if (label) {
            label.style.opacity = '';
            label.style.cursor = '';
            label.title = '';
          }
        } else {
          chk.checked = false;
          chk.disabled = true;
          if (label) {
            label.style.opacity = '0.45';
            label.style.cursor = 'not-allowed';
            const reqList = CrazyManager.getVariantRequiredPlayers(opt) || [];
            label.title = `${opt.name} requires ${reqList.join('/')} players (conflicts with ${activeVariant.name})`;
          }
          if (settings) {
            settings[opt.key] = false;
            if (opt.chkId) settings[opt.chkId] = false;
          }
        }
      } else {
        // No variant constraint active -> all restricted variants are enabled and selectable!
        chk.disabled = false;
        if (label) {
          label.style.opacity = '';
          label.style.cursor = '';
          const reqList = CrazyManager.getVariantRequiredPlayers(opt) || [];
          label.title = `${opt.name} (${reqList.join('/')} players${opt.requiredGameMode ? ' ' + opt.requiredGameMode : ''})`;
        }
      }
    });

    return { activeRestrictedVariant: activeVariant, allowedCountVal: pCountEl ? pCountEl.value : null };
  }

  /**
   * Synchronizes Player Count, Seat types, CPU availability, and Stolen Crib constraints.
   * @param {Object} [context]
   */
  static syncPlayerCountUi(context = {}) {
    if (typeof document === 'undefined') return;
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const parseGameMode = context.parseGameMode || (typeof window !== 'undefined' ? window.parseGameMode : (v => ({ playerCount: parseInt(v, 10) || 2, gameMode: 'individual' })));

    const pCountEl = document.getElementById('optPlayerCount');

    // If dropdown value was set away from what active variants require, uncheck the incompatible variants
    if (pCountEl) {
      const { playerCount: curCount, gameMode: curMode } = parseGameMode(pCountEl.value);
      CRAZY_OPTIONS_DEFS.forEach(opt => {
        if (opt.requiredPlayers != null) {
          if (!CrazyManager.isPlayerCountAllowedForVariant(opt, curCount, curMode, pCountEl.value)) {
            const chk = document.getElementById(opt.chkId || opt.key);
            if (chk && chk.checked) {
              chk.checked = false;
              if (settings) {
                settings[opt.key] = false;
                if (opt.chkId) settings[opt.chkId] = false;
              }
            }
          }
        }
      });
    }

    // Apply dynamic variant player constraints & dropdown limits
    LobbyController.syncVariantPlayerCountConstraints(context);

    let countVal = pCountEl ? pCountEl.value : '2';

    const { playerCount, gameMode } = parseGameMode(countVal);
    settings.playerCount = playerCount;
    settings.gameMode = gameMode;

    const brand = typeof context.getAppBrand === 'function' ? context.getAppBrand() : (typeof window !== 'undefined' && typeof window.getAppBrand === 'function' ? window.getAppBrand() : 'crazy');
    const GameOptionsEngine = context.GameOptionsEngine || (typeof window !== 'undefined' ? window.GameOptionsEngine : null);
    const allowCpu = GameOptionsEngine && typeof GameOptionsEngine.isCpuAllowed === 'function'
      ? GameOptionsEngine.isCpuAllowed(brand)
      : !['pints', 'adult'].includes(brand);

    const oppRow = document.getElementById('optOppRow');
    const oppSel = document.getElementById('optOpp');
    const seatPanel = document.getElementById('seatConfigPanel');
    const pBadge = document.getElementById('partnershipBadge');
    const dealSel = document.getElementById('optDeal');
    const dealRow = dealSel ? dealSel.closest('.opt') : null;
    const netPanel = document.getElementById('netPanel');

    const rawOppVal = (oppSel && oppSel.value) || 'cpu';
    let oppVal = (!allowCpu && rawOppVal === 'cpu') ? 'online' : rawOppVal;
    if (oppVal === 'human') {
      oppVal = allowCpu ? 'cpu' : 'online';
      if (oppSel) oppSel.value = oppVal;
    }
    if (oppRow) oppRow.classList.remove('hidden');
    if (netPanel) netPanel.classList.toggle('hidden', oppVal !== 'online');

    const modePills = document.querySelectorAll('.game-mode-pill');
    modePills.forEach(p => {
      p.classList.toggle('active', p.dataset.mode === oppVal);
    });

    // Seat row visibility
    for (let i = 2; i <= 8; i++) {
      const r = document.getElementById(`seatRow${i}`);
      if (r) r.style.display = i <= playerCount ? 'flex' : 'none';
    }

    if (seatPanel) {
      seatPanel.classList.toggle('hidden', playerCount <= 2);
      seatPanel.style.display = playerCount > 2 ? 'flex' : 'none';
    }

    const aiRow = document.getElementById('optAiProfileRow');
    if (aiRow) {
      const isCpu = oppVal === 'cpu';
      const showSingleCpuRow = allowCpu && isCpu && playerCount === 2;
      aiRow.classList.toggle('hidden', !showSingleCpuRow);
      aiRow.style.display = showSingleCpuRow ? '' : 'none';
    }

    if (dealRow) {
      dealRow.style.display = playerCount === 2 ? '' : 'none';
    }

    if (pBadge) {
      if (gameMode === 'partnership') {
        pBadge.textContent = 'Partnership Play (P1 & P3 vs P2 & P4)';
        pBadge.style.display = 'block';
      } else if (gameMode === 'shifting') {
        pBadge.textContent = playerCount === 3
          ? 'Shifting Partnership (P1 & P2 vs Dealer)'
          : 'Shifting Partnership (Pone & Partner vs Others)';
        pBadge.style.display = 'block';
      } else if (gameMode === 'teams') {
        pBadge.textContent = playerCount === 6
          ? '3-Team Battle (P1/P4 vs P2/P5 vs P3/P6)'
          : '4-Team Battle (P1/P5 vs P2/P6 vs P3/P7 vs P4/P8)';
        pBadge.style.display = 'block';
      } else {
        pBadge.style.display = 'none';
      }
    }

    const isPartnership = gameMode === 'partnership';
    const isTeams = gameMode === 'teams';
    const showTeamTags = isPartnership || isTeams;

    for (let i = 1; i <= 8; i++) {
      const tag = document.getElementById(`seat${i}TeamTag`);
      if (tag) {
        if (!showTeamTags || i > playerCount) {
          tag.style.display = 'none';
        } else {
          tag.style.display = 'inline-block';
          if (isPartnership) {
            if (i === 1) tag.textContent = 'Team 1';
            else if (i === 2) tag.textContent = 'Team 2';
            else if (i === 3) tag.textContent = 'Team 1 (Partner)';
            else if (i === 4) tag.textContent = 'Team 2';
          } else if (isTeams) {
            if (playerCount === 6) {
              const teamNum = ((i - 1) % 3) + 1;
              tag.textContent = `Team ${teamNum}${i === 4 ? ' (Partner)' : ''}`;
              tag.className = `seat-team-tag team${teamNum}`;
            } else if (playerCount === 8) {
              const teamNum = ((i - 1) % 4) + 1;
              tag.textContent = `Team ${teamNum}${i === 5 ? ' (Partner)' : ''}`;
              tag.className = `seat-team-tag team${teamNum}`;
            }
          }
        }
      }
    }
  }

  /**
   * Applies loaded or default settings store into form elements.
   * @param {Object} saved
   * @param {Object} [context]
   */
  static applyFormSettings(saved, context = {}) {
    if (!saved || typeof document === 'undefined') return;

    const brand = typeof context.getAppBrand === 'function' ? context.getAppBrand() : 'crazy';
    if (brand === 'clash') {
      if (!saved.target || ![241, 361, 481].includes(Number(saved.target))) {
        saved.target = 481;
      }
    } else if (brand === 'pints') {
      if (!saved.target || [241, 361, 481].includes(Number(saved.target))) {
        saved.target = 121;
      }
      if (saved.selected3DBoardId === 'board-cribbage-clash' || saved.selected3DBoardId === 'board-cribbage-clash-square') {
        saved.selected3DBoardId = 'board-14';
      }
    }
    LobbyController.disableOtherGameBonusOptions(brand, saved, context);
    LobbyController.applyBrandVisibility(brand, context);

    const GameOptionsEngine = context.GameOptionsEngine || (typeof window !== 'undefined' ? window.GameOptionsEngine : null);
    const allowCpu = GameOptionsEngine && typeof GameOptionsEngine.isCpuAllowed === 'function'
      ? GameOptionsEngine.isCpuAllowed(brand)
      : !['pints', 'adult'].includes(brand);

    if (!allowCpu) {
      if (saved.opp === 'cpu') saved.opp = 'online';
      ['seat2Type', 'seat3Type', 'seat4Type', 'seat5Type', 'seat6Type', 'seat7Type', 'seat8Type'].forEach(key => {
        if (saved[key] && typeof saved[key] === 'string' && saved[key].startsWith('cpu:')) saved[key] = 'online';
      });
    }
    if (saved.opp === 'human') saved.opp = allowCpu ? 'cpu' : 'online';
    ['seat2Type', 'seat3Type', 'seat4Type', 'seat5Type', 'seat6Type', 'seat7Type', 'seat8Type'].forEach(key => {
      if (saved[key] === 'human') saved[key] = 'online';
    });

    if (typeof context.settings !== 'undefined' && context.settings) {
      LobbyController.disableOtherGameBonusOptions(brand, context.settings, context);
      Object.assign(context.settings, saved);
      LobbyController.disableOtherGameBonusOptions(brand, context.settings, context);
    }

    if (saved.optCanadianDoubles) {
      saved.playerCount = '4_part';
      saved.gameMode = 'partnership';
    }

    if (saved.playerCount) {
      const el = document.getElementById('optPlayerCount');
      if (el && [...el.options].some(o => o.value === saved.playerCount)) el.value = saved.playerCount;
    }

    ['seat2Type', 'seat3Type', 'seat4Type', 'seat5Type', 'seat6Type', 'seat7Type', 'seat8Type'].forEach(key => {
      if (saved[key]) {
        const el = document.getElementById('opt' + key.charAt(0).toUpperCase() + key.slice(1));
        if (el && [...el.options].some(o => o.value === saved[key])) el.value = saved[key];
      }
    });

    if (saved.opp) {
      const el = document.getElementById('optOpp');
      if (el && [...el.options].some(o => o.value === saved.opp)) el.value = saved.opp;
    }
    if (saved.aiProfile) {
      const el = document.getElementById('optAiProfile');
      if (el && [...el.options].some(o => o.value === saved.aiProfile)) el.value = saved.aiProfile;
    }
    if (saved.coach) {
      const el = document.getElementById('optCoach');
      if (el && [...el.options].some(o => o.value === saved.coach)) el.value = saved.coach;
    }
    if (saved.cardAssassin) {
      const el = document.getElementById('optCardAssassin');
      if (el && [...el.options].some(o => o.value === saved.cardAssassin)) el.value = saved.cardAssassin;
    }
    if (saved.jailAnimation) {
      const el = document.getElementById('optJailAnimation');
      if (el && [...el.options].some(o => o.value === saved.jailAnimation)) el.value = saved.jailAnimation;
    }
    if (saved.barAnimation) {
      const el = document.getElementById('optBarAnimation');
      if (el && [...el.options].some(o => o.value === saved.barAnimation)) el.value = saved.barAnimation;
    }
    if (saved.cannonAnimation) {
      const el = document.getElementById('optCannonAnimation');
      if (el && [...el.options].some(o => o.value === saved.cannonAnimation)) el.value = saved.cannonAnimation;
    }
    if (saved.speedTrapAnimation) {
      const el = document.getElementById('optSpeedTrapAnimation');
      if (el && [...el.options].some(o => o.value === saved.speedTrapAnimation)) el.value = saved.speedTrapAnimation;
    }
    if (saved.scenicRouteAnimation) {
      const el = document.getElementById('optScenicRouteAnimation');
      if (el && [...el.options].some(o => o.value === saved.scenicRouteAnimation)) el.value = saved.scenicRouteAnimation;
    }
    if (saved.detourAnimation || saved.scenicRouteAnimation) {
      const el = document.getElementById('optDetourAnimation');
      const val = saved.detourAnimation || saved.scenicRouteAnimation;
      if (el && [...el.options].some(o => o.value === val)) el.value = val;
    }
    if (saved.roundAboutAnimation || saved.scenicRouteAnimation) {
      const el = document.getElementById('optRoundAboutAnimation');
      const val = saved.roundAboutAnimation || saved.scenicRouteAnimation;
      if (el && [...el.options].some(o => o.value === val)) el.value = val;
    }
    if (saved.deal) {
      const el = document.getElementById('optDeal');
      if (el && [...el.options].some(o => o.value === saved.deal)) el.value = saved.deal;
    }
    if (saved.dealStyle) {
      const el = document.getElementById('optDealStyle');
      if (el) el.value = 'alternate';
    }
    if (saved.showDetail) {
      const el = document.getElementById('optShowDetail');
      if (el && [...el.options].some(o => o.value === saved.showDetail)) el.value = saved.showDetail;
    } else if (saved.optMuggins) {
      const el = document.getElementById('optShowDetail');
      if (el) el.value = 'muggins';
    }
    if (brand === 'clash') {
      const el = document.getElementById('optGoal');
      if (el) el.value = 'high';
    } else if (saved.goal) {
      const el = document.getElementById('optGoal');
      if (el && [...el.options].some(o => o.value === saved.goal)) el.value = saved.goal;
    }

    if (saved.target != null) {
      const el = document.getElementById('optTarget');
      let v = String(saved.target);
      if (brand === 'clash' && !['241', '361', '481'].includes(v)) {
        v = '481';
      }
      if (brand !== 'clash' && (v === '361' || v === '481')) {
        v = '121';
      }
      if ((brand === 'pints' || brand === 'adult') && v === '241') {
        v = '121';
      }
      if (el) {
        if (![...el.options].some(o => o.value === v)) {
          LobbyController.updateTargetOptions(brand, context);
        }
        el.value = v;
      }
      const targetVal = el ? parseInt(el.value, 10) : parseInt(v, 10);
      if (context.settings) {
        context.settings.target = targetVal;
      }
      const isClashTarget = (brand === 'clash') && ['241', '361', '481'].includes(v);
      const bClash = document.getElementById('cribbage-clash-options');
      if (bClash) bClash.style.display = isClashTarget ? 'block' : 'none';
      if (typeof context.updateCribbageClashOptionRanges === 'function') {
        context.updateCribbageClashOptionRanges(targetVal);
      }
    }

    if (saved.decks != null) {
      const el = document.getElementById('optDecks');
      const v = String(saved.decks);
      if (el && [...el.options].some(o => o.value === v)) el.value = v;
    }

    if (saved.cardBack && typeof context.setCardBack === 'function') {
      context.setCardBack(saved.cardBack, true);
    }
    if (saved.boardFinish) {
      const el = document.getElementById('optBoardFinish');
      if (el) el.value = saved.boardFinish;
      if (context.settings) context.settings.boardFinish = saved.boardFinish;
    }
    if (saved.pegSkin) {
      const el = document.getElementById('optPegSkin');
      if (el) el.value = saved.pegSkin;
      if (context.settings) context.settings.pegSkin = saved.pegSkin;
    }
    if (saved.pegStyle) {
      const el = document.getElementById('optPegStyle');
      if (el) el.value = saved.pegStyle;
      if (context.settings) context.settings.pegStyle = saved.pegStyle;
    }
    if (saved.tableEnvironment) {
      const el = document.getElementById('optTableEnvironment');
      if (el) el.value = saved.tableEnvironment;
      if (context.settings) context.settings.tableEnvironment = saved.tableEnvironment;
    }
    if (saved.optActionCam) {
      const el = document.getElementById('optActionCam');
      if (el) el.value = saved.optActionCam;
      if (context.settings) context.settings.optActionCam = saved.optActionCam;
    }
    if (saved.seriesMode) {
      const el = document.getElementById('optSeriesMode');
      if (el) el.value = saved.seriesMode;
      if (context.settings) context.settings.seriesMode = saved.seriesMode;
      SeriesTracker.setMode(saved.seriesMode);
    }
    if (saved.boardViewMode) {
      const el = document.getElementById('optBoardView');
      if (el) el.value = saved.boardViewMode;
      const elModal = document.getElementById('optBoardViewModal');
      if (elModal) elModal.value = saved.boardViewMode;
      if (context.settings) context.settings.boardViewMode = saved.boardViewMode;
    }
    const el3d = document.getElementById('opt3DBoard');
    if (el3d) {
      const bId = saved?.selected3DBoardId || context.settings?.selected3DBoardId || (brand === 'clash' ? 'board-cribbage-clash' : 'board-14');
      Board3DCatalog.syncDropdownOptions(el3d, bId, brand);
      if (context.settings) context.settings.selected3DBoardId = el3d.value;
      if (typeof window !== 'undefined' && window.settings) window.settings.selected3DBoardId = el3d.value;
    }

    const container = document.getElementById('brand-game-options');
    if (container && GameOptionsEngine) {
      if (container.getAttribute('data-rendered-brand') !== brand) {
        GameOptionsEngine.buildBrandSettingsUI(brand, container);
        container.setAttribute('data-rendered-brand', brand);
      }
      GameOptionsEngine.applyBrandSettings(brand, saved);
    }

    if (typeof context.updateCheckAllBonusesState === 'function') {
      context.updateCheckAllBonusesState();
    }
    LobbyController.syncPlayerCountUi(context);
    if (typeof context.enforceClashNoOverlap === 'function') {
      context.enforceClashNoOverlap();
    }
    LobbyController.applyBrandVisibility(brand, context);
    if (typeof context.syncAllClashPlacementButtons === 'function') {
      context.syncAllClashPlacementButtons();
    }
  }

  /**
   * Builds the roster data structure for lobby display and online room synchronization.
   * @param {Object} [context]
   * @returns {Array<Object>}
   */
  static getLobbyRosterData(context = {}) {
    if (typeof document === 'undefined') return [];
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const net = context.net || (typeof window !== 'undefined' ? window.net : {});
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : (() => 'p1'));
    const parseGameMode = context.parseGameMode || (typeof window !== 'undefined' ? window.parseGameMode : (v => ({ playerCount: parseInt(v, 10) || 2 })));

    const pCountEl = document.getElementById('optPlayerCount');
    const countVal = pCountEl ? pCountEl.value : '2';
    const { playerCount } = parseGameMode(countVal);
    const count = playerCount || settings.playerCount || 2;
    const list = [];
    const hostName = (document.getElementById('optPlayerName') && document.getElementById('optPlayerName').value.trim()) || 'Host';
    const myCurrentSeat = mySeat();

    const seatToClient = new Map();
    if (net.clients) {
      for (const client of net.clients.values()) {
        if (client.seatId) seatToClient.set(client.seatId, client);
      }
    }

    for (let i = 1; i <= count; i++) {
      const sId = 'p' + i;
      const isMe = sId === myCurrentSeat;
      if (sId === 'p1') {
        list.push({
          id: 'p1',
          num: 1,
          name: hostName,
          type: 'human',
          status: 'host',
          statusText: isMe ? 'Host (You)' : 'Host (Online)',
          isYou: isMe
        });
      } else {
        const client = seatToClient.get(sId);
        if (client) {
          list.push({
            id: sId,
            num: i,
            name: client.name || `Player ${i}`,
            type: 'human',
            status: 'online',
            statusText: isMe ? 'Online (You)' : 'Online',
            isYou: isMe
          });
        } else if (settings.netRole === 'guest') {
          list.push({
            id: sId,
            num: i,
            name: isMe ? ((document.getElementById('optPlayerName') && document.getElementById('optPlayerName').value.trim()) || `Player ${i}`) : `Player ${i}`,
            type: isMe ? 'human' : 'cpu',
            status: isMe ? 'online' : 'waiting',
            statusText: isMe ? 'Online (You)' : 'Waiting…',
            isYou: isMe
          });
        } else {
          const defaultBotName = { p2: 'The Shark', p3: 'The Gambler', p4: 'The Turtler', p5: 'The Gambler', p6: 'The Turtler', p7: 'The Shark', p8: 'The Gambler' }[sId] || `Bot ${i}`;
          list.push({
            id: sId,
            num: i,
            name: defaultBotName,
            type: 'cpu',
            status: 'waiting',
            statusText: 'Open (or Bot)',
            isYou: false
          });
        }
      }
    }
    return list;
  }

  /**
   * Resolves seat configurations for a match given player count, CPU allowance, and UI option values.
   * @param {number|string} playerCount
   * @param {boolean} allowCpu
   * @param {Object} [options]
   * @returns {Array<Object>}
   */
  static resolveSeatConfigs(playerCount, allowCpu = true, options = {}) {
    const pCountNum = parseInt(playerCount, 10) || 2;
    const opp = options.opp || 'cpu';
    const aiProfile = options.aiProfile || 'shark';
    const seatValues = options.seatValues || {};

    const parseSeatVal = (val, defaultPers, defaultName, team, seatNum) => {
      if (!allowCpu || val === 'human') return { type: 'human', personality: defaultPers, name: defaultName, team };
      const parts = (val || '').split(':');
      const pers = parts[1] || defaultPers;
      let botName = '';
      if (typeof document !== 'undefined' && seatNum) {
        const el = document.getElementById(`optSeat${seatNum}Type`);
        if (el && el.selectedIndex >= 0) {
          const text = el.options[el.selectedIndex]?.textContent || '';
          const m = text.match(/(?:[—–-]\s*)([^(]+)/);
          if (m && m[1].trim()) {
            botName = m[1].trim();
          }
        }
      }
      const avatarMap = {
        expert: 'shark',
        shark: 'shark',
        master: 'king',
        advanced: 'turtler',
        turtler: 'turtler',
        maverick: 'steampunk',
        intermediate: 'gambler',
        gambler: 'gambler',
        regular: 'velvet_fox',
        beginner: 'jack',
        rookie: 'jack',
        casual: 'pint_master'
      };
      if (!botName || botName.startsWith('Player')) {
        const defaultBotNames = { 2: 'The Shark', 3: 'The Regular', 4: 'The Turtler', 5: 'The Maverick', 6: 'The Grandmaster', 7: 'The Rookie', 8: 'The Casual' };
        const nameMap = {
          expert: 'The Shark',
          shark: 'The Shark',
          master: 'The Grandmaster',
          advanced: 'The Turtler',
          turtler: 'The Turtler',
          maverick: 'The Maverick',
          intermediate: 'The Gambler',
          gambler: 'The Gambler',
          regular: 'The Regular',
          beginner: 'The Rookie',
          rookie: 'The Rookie',
          casual: 'The Casual'
        };
        botName = nameMap[pers] || (defaultBotNames && defaultBotNames[seatNum]) || 'Computer';
      }
      const botAvatar = avatarMap[pers] || 'jack';
      return { type: 'cpu', personality: pers, name: botName, avatar: botAvatar, team };
    };

    if (pCountNum === 2) {
      const oppType = allowCpu ? opp : 'human';
      const nameMap = {
        expert: 'The Shark',
        shark: 'The Shark',
        master: 'The Grandmaster',
        advanced: 'The Turtler',
        turtler: 'The Turtler',
        maverick: 'The Maverick',
        intermediate: 'The Gambler',
        gambler: 'The Gambler',
        regular: 'The Regular',
        beginner: 'The Rookie',
        rookie: 'The Rookie',
        casual: 'The Casual'
      };
      const avatarMap = {
        expert: 'shark',
        shark: 'shark',
        master: 'king',
        advanced: 'turtler',
        turtler: 'turtler',
        maverick: 'steampunk',
        intermediate: 'gambler',
        gambler: 'gambler',
        regular: 'velvet_fox',
        beginner: 'jack',
        rookie: 'jack',
        casual: 'pint_master'
      };
      const botName = nameMap[aiProfile] || 'Computer';
      const botAvatar = avatarMap[aiProfile] || 'shark';
      return [
        { id: 'p1', name: 'You', type: 'human', personality: 'shark', team: 1, color: '#d4a017' },
        { id: 'p2', name: oppType === 'cpu' ? botName : 'Player 2', type: oppType === 'cpu' ? 'cpu' : 'human', personality: aiProfile || 'shark', avatar: oppType === 'cpu' ? botAvatar : 'queen', team: 2, color: '#ef4444' },
        { id: 'p3', name: 'Seat 3', type: allowCpu ? 'cpu' : 'human', personality: 'regular', avatar: 'velvet_fox', team: 1, color: '#10b981' },
        { id: 'p4', name: 'Seat 4', type: allowCpu ? 'cpu' : 'human', personality: 'turtler', avatar: 'turtler', team: 2, color: '#3b82f6' },
        { id: 'p5', name: 'Seat 5', type: allowCpu ? 'cpu' : 'human', personality: 'maverick', avatar: 'steampunk', team: 1, color: '#a855f7' },
        { id: 'p6', name: 'Seat 6', type: allowCpu ? 'cpu' : 'human', personality: 'master', avatar: 'king', team: 2, color: '#f97316' },
        { id: 'p7', name: 'Seat 7', type: allowCpu ? 'cpu' : 'human', personality: 'rookie', avatar: 'jack', team: 3, color: '#06b6d4' },
        { id: 'p8', name: 'Seat 8', type: allowCpu ? 'cpu' : 'human', personality: 'casual', avatar: 'pint_master', team: 4, color: '#ec4899' }
      ];
    }

    const s2Val = seatValues.s2 || (typeof document !== 'undefined' && document.getElementById('optSeat2Type') && document.getElementById('optSeat2Type').value) || 'cpu:shark';
    const s3Val = seatValues.s3 || (typeof document !== 'undefined' && document.getElementById('optSeat3Type') && document.getElementById('optSeat3Type').value) || 'cpu:regular';
    const s4Val = seatValues.s4 || (typeof document !== 'undefined' && document.getElementById('optSeat4Type') && document.getElementById('optSeat4Type').value) || 'cpu:turtler';
    const s5Val = seatValues.s5 || (typeof document !== 'undefined' && document.getElementById('optSeat5Type') && document.getElementById('optSeat5Type').value) || 'cpu:maverick';
    const s6Val = seatValues.s6 || (typeof document !== 'undefined' && document.getElementById('optSeat6Type') && document.getElementById('optSeat6Type').value) || 'cpu:master';
    const s7Val = seatValues.s7 || (typeof document !== 'undefined' && document.getElementById('optSeat7Type') && document.getElementById('optSeat7Type').value) || 'cpu:rookie';
    const s8Val = seatValues.s8 || (typeof document !== 'undefined' && document.getElementById('optSeat8Type') && document.getElementById('optSeat8Type').value) || 'cpu:casual';

    const s2 = parseSeatVal(s2Val, 'shark', 'Player 2', 2, 2);
    const s3 = parseSeatVal(s3Val, 'regular', 'Player 3', 1, 3);
    const s4 = parseSeatVal(s4Val, 'turtler', 'Player 4', 2, 4);
    const s5 = parseSeatVal(s5Val, 'maverick', 'Player 5', 1, 5);
    const s6 = parseSeatVal(s6Val, 'master', 'Player 6', 3, 6);
    const s7 = parseSeatVal(s7Val, 'rookie', 'Player 7', 3, 7);
    const s8 = parseSeatVal(s8Val, 'casual', 'Player 8', 4, 8);

    return [
      { id: 'p1', name: 'You', type: 'human', personality: 'shark', team: 1, color: '#d4a017' },
      { id: 'p2', name: s2.name, type: s2.type, personality: s2.personality, avatar: s2.avatar, team: 2, color: '#ef4444' },
      { id: 'p3', name: s3.name, type: s3.type, personality: s3.personality, avatar: s3.avatar, team: 1, color: '#10b981' },
      { id: 'p4', name: s4.name, type: s4.type, personality: s4.personality, avatar: s4.avatar, team: 2, color: '#3b82f6' },
      { id: 'p5', name: s5.name, type: s5.type, personality: s5.personality, avatar: s5.avatar, team: 1, color: '#a855f7' },
      { id: 'p6', name: s6.name, type: s6.type, personality: s6.personality, avatar: s6.avatar, team: 3, color: '#f97316' },
      { id: 'p7', name: s7.name, type: s7.type, personality: s7.personality, avatar: s7.avatar, team: 3, color: '#06b6d4' },
      { id: 'p8', name: s8.name, type: s8.type, personality: s8.personality, avatar: s8.avatar, team: 4, color: '#ec4899' }
    ];
  }

  /**
   * Generates the list of mode pills badges for the match header.
   * @param {Object} settings
   * @param {Object} [options]
   * @returns {Array<string>}
   */
  static generateModePills(settings = {}, options = {}) {
    const pCountNum = parseInt(settings.playerCount, 10) || 2;
    const allowCpu = options.allowCpu != null ? options.allowCpu : true;
    const isOnline = options.isOnline || (typeof window !== 'undefined' && window.isOnline ? window.isOnline : () => false);
    const isHvH = options.isHvH || (typeof window !== 'undefined' && window.isHvH ? window.isHvH : () => false);
    const is3Player1vs2 = options.is3Player1vs2 || (typeof window !== 'undefined' && window.is3Player1vs2 ? window.is3Player1vs2 : () => false);
    const getNoPassingLanes = options.getNoPassingLanes || (typeof window !== 'undefined' && window.getNoPassingLanes ? window.getNoPassingLanes : () => []);
    const getJumpConfigs = options.getJumpConfigs || (typeof window !== 'undefined' && window.getJumpConfigs ? window.getJumpConfigs : () => []);
    const getJailConfigs = options.getJailConfigs || (typeof window !== 'undefined' && window.getJailConfigs ? window.getJailConfigs : () => []);
    const getBarConfigs = options.getBarConfigs || (typeof window !== 'undefined' && window.getBarConfigs ? window.getBarConfigs : () => []);
    const getTimeoutConfigs = options.getTimeoutConfigs || (typeof window !== 'undefined' && window.getTimeoutConfigs ? window.getTimeoutConfigs : () => []);
    const getDetourConfigs = options.getDetourConfigs || (typeof window !== 'undefined' && window.getDetourConfigs ? window.getDetourConfigs : () => []);
    const getRoundAboutConfigs = options.getRoundAboutConfigs || (typeof window !== 'undefined' && window.getRoundAboutConfigs ? window.getRoundAboutConfigs : () => []);
    const getSignConfigs = options.getSignConfigs || (typeof window !== 'undefined' && window.getSignConfigs ? window.getSignConfigs : () => []);

    const pills = [];
    if (pCountNum === 2) {
      if (!allowCpu) {
        pills.push(isOnline() ? 'Online' : 'Human vs human');
      } else {
        pills.push(isOnline() ? 'Online' : (isHvH() ? 'Human vs human' : 'vs computer'));
        if (settings.opp === 'cpu') {
          const pLabel = {
            shark: 'Shark AI',
            gambler: 'Gambler AI',
            turtler: 'Turtler AI'
          }[settings.aiProfile] || 'Shark AI';
          pills.push(pLabel);
        }
      }
    } else if (pCountNum === 3) {
      pills.push(is3Player1vs2() ? '3 Players (1 vs 2)' : '3 Players (Individual)');
    } else if (pCountNum === 4 && settings.gameMode === 'indiv') {
      pills.push('4 Players (Cutthroat)');
    } else if (pCountNum === 4 && settings.gameMode === 'partnership') {
      pills.push('4 Players (Partnership 2v2)');
    } else if (pCountNum === 5) {
      pills.push('5 Players (1 vs 2 Pairs)');
    } else if (pCountNum === 6) {
      pills.push('6 Players (3 Teams of 2)');
    } else if (pCountNum === 7) {
      pills.push('7 Players (1 vs 3 Pairs)');
    } else if (pCountNum === 8) {
      pills.push('8 Players (4 Teams of 2)');
    }

    if (settings.coach === 'on') pills.push('Coach on');

    if (settings.optCanadianDoubles && pCountNum === 4) {
      pills.push('Deal (10 / keep 4 / pass 4 / crib 2)');
    } else if (settings.playerCount >= 3) {
      pills.push('Deal (5 / discard 1)');
    } else {
      pills.push(settings.deal === 'super' ? 'Super (9 / discard 3)' : 'Standard (6 / discard 2)');
    }
    pills.push(settings.decks === 2 ? '2 decks' : '1 deck');

    const currentBrand = options.brand || (typeof options.getAppBrand === 'function' ? options.getAppBrand() : (settings.brand || (typeof window !== 'undefined' && typeof window.getAppBrand === 'function' ? window.getAppBrand() : 'crazy')));

    if (currentBrand === 'crazy') {
      if (settings.jokers) pills.push('jokers wild');
      if (settings.optCutBeforeDiscard) pills.push('cut before discard');
      if (settings.opt31sFor5) pills.push("31's for 5");
      if (settings.opt19sFor2) pills.push("19's for 2");
      if (settings.optRoundAroundTheWorld) pills.push('round around the world');
      if (settings.optMultipleRunsInPegging) pills.push('multiple runs in pegging');
      if (settings.optPickYourCrib) pills.push('pick your crib');
      if (settings.optSpeedoCribbage && settings.playerCount === 2) pills.push('speedo cribbage');
      if (settings.optShotgunCribbage) pills.push('shotgun cribbage');
      if (settings.optTwistedCribbage && settings.playerCount === 2) pills.push('twisted cribbage');
      if (settings.optChutesAndLadders) pills.push('chutes and ladders');
      if (settings.optFivesCribbage) pills.push("five's cribbage");
      if (settings.optSevensAndEightsCribbage) pills.push('sevens and eights cribbage');
      if (settings.optDrawCribbage && settings.playerCount === 2) pills.push('draw cribbage');
      if (settings.opt7CardCribbage && settings.playerCount === 2) pills.push('7 card cribbage');
      if (settings.optProgressiveCut && settings.playerCount === 2) pills.push('progressive cut');
      if (settings.optCanadianDoubles && settings.playerCount === 4) pills.push('canadian doubles');
      if (settings.optProgressiveSteals) pills.push('progressive steals');
      if (settings.optAllRed) pills.push('all red');
      if (settings.optAllBlack) pills.push('all black');
      if (settings.optAllOdd) pills.push('all odd');
      if (settings.optAllEven) pills.push('all even');
      if (settings.optAllDifferentSuits) pills.push('diff suits');
      if (settings.optKH) pills.push('KH +5');
      if (settings.optQS) pills.push('QS +5');
      if (settings.optAS) pills.push('AS +5');
      if (settings.optSpades) pills.push('spades');
      if (settings.optGuessStarter) pills.push('guess starter');
      if (settings.optStraight) pills.push('straight +10');
      if (settings.optFullHouse) pills.push('full house +10');
      if (settings.optStraightFlush) pills.push('straight flush +20');
      if (settings.optRoyalFlush) pills.push('royal flush +30');
      if (settings.optFlushInPlay) pills.push('flush in play');
      if (settings.optRockCrib) pills.push('rock the crib');
      if (settings.optStinkHole) pills.push('stink hole');
      if (settings.optPegThroughZones) pills.push('peg through zones');
      if (settings.optLast5PegOut) pills.push('last 5 peg out');
      if (settings.optBunkers) pills.push('bunkers');
      if (settings.optStolenCrib && settings.playerCount === 2) pills.push('stolen crib');
      if (settings.optZeroHand) pills.push('zero hand −19');
      if (settings.optZeroCrib) pills.push('zero crib −19');
    } else if (currentBrand === 'clash') {
      if (settings.optNoPassingLanes) {
        const laneCount = getNoPassingLanes().length;
        pills.push(`no passing lanes (${laneCount})`);
      }
      if (settings.optJump) {
        const jCount = getJumpConfigs().length;
        pills.push(`jump (${jCount})`);
      }
      if (settings.optJail) {
        const jailCount = getJailConfigs().length;
        pills.push(`jail (${jailCount})`);
      }
      if (settings.optBar) {
        const barCount = getBarConfigs().length;
        pills.push(`the bar (${barCount})`);
      }
      if (settings.optTimeout) {
        const timeoutCount = getTimeoutConfigs().length;
        pills.push(`timeout (${timeoutCount})`);
      }
      if (settings.optDetour) {
        const detourCount = getDetourConfigs().length;
        pills.push(`detour (${detourCount})`);
      }
      if (settings.optRoundAbout) {
        const roundAboutCount = getRoundAboutConfigs().length;
        pills.push(`round about (${roundAboutCount})`);
      }
      if (settings.optSigns) {
        const signsCount = getSignConfigs().length;
        pills.push(`signs (${signsCount})`);
      }
      if (settings.optDice) {
        const getDice = options.getDiceConfigs || (typeof window !== 'undefined' && window.getDiceConfigs ? window.getDiceConfigs : () => []);
        const diceCount = getDice().length;
        pills.push(`dice (${diceCount || settings.diceCount || 4})`);
      }
      if (settings.optWheel || settings.optSpinningWheel) {
        const getWheels = options.getWheelConfigs || (typeof window !== 'undefined' && window.getWheelConfigs ? window.getWheelConfigs : () => []);
        const wheelCount = getWheels().length;
        pills.push(`spinning wheel (${wheelCount || settings.wheelCount || 4})`);
      }
      if (settings.optSkunkMarkers) pills.push('skunk markers');
    }

    pills.push(settings.goal === 'low' ? 'Low ball' : 'High ball');
    if ((settings.brand === 'clash' || currentBrand === 'clash') && [241, 361, 481].includes(settings.target)) {
      pills.push('Cribbage Clash (to ' + settings.target + ')');
    } else if (settings.target === 241) {
      pills.push('to 241 (There and Back)');
    } else {
      pills.push('to ' + (settings.target || 121));
    }
    return pills;
  }

  /**
   * Enforces mutual exclusivity among zone options (optPegThroughZones, optLast5PegOut, optBunkers, optStinkHole).
   * @param {HTMLElement} changedEl
   * @param {Object} [context]
   */
  static handleZoneOptionChange(changedEl, context = {}) {
    if (!changedEl || !changedEl.checked) return;
    const updateCheckAllBonusesState = context.updateCheckAllBonusesState || (typeof window !== 'undefined' ? window.updateCheckAllBonusesState : () => {});
    const zoneIds = ['optPegThroughZones', 'optLast5PegOut', 'optBunkers', 'optStinkHole'];
    if (typeof document !== 'undefined') {
      zoneIds.forEach(id => {
        if (id !== changedEl.id) {
          const other = document.getElementById(id);
          if (other) other.checked = false;
        }
      });
    }
    updateCheckAllBonusesState();
    if (typeof window !== 'undefined' && typeof window.saveSettingsStore === 'function' && typeof window.readFormSettings === 'function') {
      window.saveSettingsStore(window.readFormSettings());
    }
  }

  static handleCribOptionChange(changedEl, context = {}) {
    if (!changedEl || typeof document === 'undefined') return;
    const curSettings = context.settings || (typeof window !== 'undefined' ? window.settings : null);

    if (!changedEl.checked) {
      if (curSettings) {
        const optDef = CRAZY_OPTIONS_DEFS.find(o => o.chkId === changedEl.id || o.key === changedEl.id);
        if (optDef) {
          curSettings[optDef.key] = false;
          if (optDef.chkId) curSettings[optDef.chkId] = false;
        }
      }
      LobbyController.syncVariantPlayerCountConstraints({ ...context, changedEl });
      const updateCheckAllBonusesState = context.updateCheckAllBonusesState || (typeof window !== 'undefined' ? window.updateCheckAllBonusesState : () => {});
      updateCheckAllBonusesState();
      return;
    }

    const updateCheckAllBonusesState = context.updateCheckAllBonusesState || (typeof window !== 'undefined' ? window.updateCheckAllBonusesState : () => {});
    if (changedEl.id === 'optShotgunCribbage') {
      ['optRockCrib', 'optZeroCrib', 'optStolenCrib', 'optTwistedCribbage'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.checked = false;
      });
    } else if (['optRockCrib', 'optZeroCrib', 'optStolenCrib'].includes(changedEl.id)) {
      const el = document.getElementById('optShotgunCribbage');
      if (el) el.checked = false;
    } else if (changedEl.id === 'optTwistedCribbage') {
      ['optShotgunCribbage', 'opt7CardCribbage'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.checked = false;
      });
    }
    if (changedEl.id === 'opt7CardCribbage') {
      ['optSpeedoCribbage', 'optPickYourCrib', 'optDrawCribbage', 'optTwistedCribbage'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.checked = false;
      });
    } else if (changedEl.id === 'optDrawCribbage') {
      ['optSpeedoCribbage', 'optPickYourCrib', 'optFivesCribbage', 'optSevensAndEightsCribbage', 'opt7CardCribbage'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.checked = false;
      });
    } else if (changedEl.id === 'optFivesCribbage') {
      ['optSpeedoCribbage', 'optPickYourCrib', 'optSevensAndEightsCribbage', 'optDrawCribbage'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.checked = false;
      });
    } else if (changedEl.id === 'optSevensAndEightsCribbage') {
      ['optSpeedoCribbage', 'optPickYourCrib', 'optFivesCribbage', 'optDrawCribbage'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.checked = false;
      });
    } else if (['optSpeedoCribbage', 'optPickYourCrib'].includes(changedEl.id)) {
      const el5 = document.getElementById('optFivesCribbage');
      if (el5) el5.checked = false;
      const el78 = document.getElementById('optSevensAndEightsCribbage');
      if (el78) el78.checked = false;
      const elDraw = document.getElementById('optDrawCribbage');
      if (elDraw) elDraw.checked = false;
      const el7Card = document.getElementById('opt7CardCribbage');
      if (el7Card) el7Card.checked = false;
    }

    const res = LobbyController.syncVariantPlayerCountConstraints({ ...context, changedEl });
    if (res.activeRestrictedVariant) {
      const pCountEl = document.getElementById('optPlayerCount');
      if (pCountEl) {
        pCountEl.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (context.syncPlayerCountUi) {
        context.syncPlayerCountUi();
      } else if (typeof window !== 'undefined' && typeof window.syncPlayerCountUi === 'function') {
        window.syncPlayerCountUi();
      } else {
        LobbyController.syncPlayerCountUi(context);
      }
    }

    updateCheckAllBonusesState();
    if (typeof window !== 'undefined' && typeof window.saveSettingsStore === 'function' && typeof window.readFormSettings === 'function') {
      window.saveSettingsStore(window.readFormSettings());
    }
  }

  handleZoneOptionChange(changedEl, context) {
    return LobbyController.handleZoneOptionChange(changedEl, context);
  }

  /**
   * Randomizes the bonus options for Crazy Cribbage.
   * @param {Object} [context]
   */
  static randomizeCrazyCribbageBonuses(context = {}) {
    const GameOptionsEngine = context.GameOptionsEngine || (typeof window !== 'undefined' ? window.GameOptionsEngine : null);
    if (GameOptionsEngine && typeof GameOptionsEngine.randomizeBrandOptions === 'function') {
      return GameOptionsEngine.randomizeBrandOptions('crazy', false);
    }
    if (typeof document === 'undefined') return;
    const items = document.querySelectorAll('.crazy-only-options input');
    items.forEach(opt => opt.checked = false);
    const getRandomSubset = context.getRandomSubset || (typeof window !== 'undefined' ? window.getRandomSubset : (arr) => arr.filter(() => Math.random() > 0.5));
    const rand = getRandomSubset(items);
    rand.forEach(item => item.checked = true);
  }

  /**
   * Checks age verification on startup for brands requiring age gating.
   * @param {Object} [context]
   */
  static checkInitialAgeGate(context = {}) {
    const getAppBrand = context.getAppBrand || (typeof window !== 'undefined' ? window.getAppBrand : () => 'crazy');
    const isAgeVerified = context.isAgeVerified || (typeof window !== 'undefined' ? window.isAgeVerified : () => false);
    const showAgeGateModal = context.showAgeGateModal || (typeof window !== 'undefined' ? window.showAgeGateModal : () => {});
    const brand = getAppBrand();
    if ((brand === 'pints' || brand === 'adult') && !isAgeVerified(brand)) {
      showAgeGateModal(brand);
    }
  }

  /**
   * Wires master 'Check All Bonuses' checkbox and handles individual option change events.
   * @param {Object} [context]
   */
  static setupCheckAllBonuses(context = {}) {
    if (typeof document === 'undefined') return;
    const updateCheckAllBonusesState = context.updateCheckAllBonusesState || (typeof window !== 'undefined' ? window.updateCheckAllBonusesState : () => {});
    const parseGameMode = context.parseGameMode || (typeof window !== 'undefined' ? window.parseGameMode : (v => ({ playerCount: parseInt(v, 10) || 2 })));
    const toggleSignsConfig = context.toggleSignsConfig || (typeof window !== 'undefined' ? window.toggleSignsConfig : () => {});
    const toggleDetourConfig = context.toggleDetourConfig || (typeof window !== 'undefined' ? window.toggleDetourConfig : () => {});
    const toggleRoundAboutConfig = context.toggleRoundAboutConfig || (typeof window !== 'undefined' ? window.toggleRoundAboutConfig : () => {});
    const handleZoneOptionChange = context.handleZoneOptionChange || (typeof window !== 'undefined' ? window.handleZoneOptionChange : LobbyController.handleZoneOptionChange);

    const checkAllBonusesEl = document.getElementById('checkAllBonuses');
    if (checkAllBonusesEl) {
      const handleCheckAllToggle = () => {
        const checkedState = checkAllBonusesEl.checked;
        const opts = document.querySelectorAll('.bonuses .chk input');
        const countVal = (document.getElementById('optPlayerCount') && document.getElementById('optPlayerCount').value) || '2';
        const { playerCount, gameMode } = parseGameMode(countVal);
        opts.forEach(opt => {
          const def = CRAZY_OPTIONS_DEFS.find(d => d.chkId === opt.id || d.key === opt.id);
          if (checkedState && (opt.id === 'optLast5PegOut' || opt.id === 'optBunkers' || opt.id === 'optStinkHole' || opt.id === 'optShotgunCribbage')) {
            opt.checked = false;
          } else if (checkedState && def && def.requiredPlayers != null && !CrazyManager.isPlayerCountAllowedForVariant(def, playerCount, gameMode, countVal)) {
            opt.checked = false;
          } else {
            opt.checked = checkedState;
          }
          if (opt.id === 'optSigns') toggleSignsConfig(opt.checked);
          if (opt.id === 'optDetour') toggleDetourConfig(opt.checked);
          if (opt.id === 'optRoundAbout') toggleRoundAboutConfig(opt.checked);
        });
        LobbyController.syncVariantPlayerCountConstraints(context);
        const GameOptionsEngine = context.GameOptionsEngine || (typeof window !== 'undefined' ? window.GameOptionsEngine : null) || (typeof globalThis !== 'undefined' ? globalThis.GameOptionsEngine : null);
        if (GameOptionsEngine && typeof GameOptionsEngine.updateSubgroupActiveBadges === 'function') {
          GameOptionsEngine.updateSubgroupActiveBadges('crazy', document.getElementById('brand-game-options'));
        }
        if (typeof window !== 'undefined' && typeof window.saveSettingsStore === 'function' && typeof window.readFormSettings === 'function') {
          window.saveSettingsStore(window.readFormSettings());
        }
      };
      checkAllBonusesEl.onclick = handleCheckAllToggle;
      checkAllBonusesEl.addEventListener('change', handleCheckAllToggle);
    }

    document.querySelectorAll('.bonuses .chk input').forEach(opt => {
      opt.addEventListener('change', updateCheckAllBonusesState);
    });

    ['optPegThroughZones', 'optLast5PegOut', 'optBunkers', 'optStinkHole'].forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener('change', () => handleZoneOptionChange(el, context));
      }
    });

    ['optShotgunCribbage', 'optRockCrib', 'optZeroCrib', 'optStolenCrib', 'optTwistedCribbage', 'optFivesCribbage', 'optSevensAndEightsCribbage', 'optDrawCribbage', 'optSpeedoCribbage', 'optPickYourCrib', 'opt7CardCribbage', 'optProgressiveCut', 'optCanadianDoubles'].forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener('change', () => LobbyController.handleCribOptionChange(el, context));
      }
    });

    if (!LobbyController._variantConstraintsDelegatedAttached) {
      LobbyController._variantConstraintsDelegatedAttached = true;
      document.addEventListener('change', (e) => {
        if (e.target && (e.target.id === 'optCanadianDoubles' || CRAZY_OPTIONS_DEFS.some(o => o.chkId === e.target.id || o.key === e.target.id))) {
          LobbyController.handleCribOptionChange(e.target, context);
        }
      });
    }

    const pCountSelectEl = document.getElementById('optPlayerCount');
    if (pCountSelectEl && !pCountSelectEl._variantConstraintsSyncBound) {
      pCountSelectEl._variantConstraintsSyncBound = true;
      pCountSelectEl.addEventListener('change', () => {
        const { playerCount: newCount, gameMode: newMode } = parseGameMode(pCountSelectEl.value);
        CRAZY_OPTIONS_DEFS.forEach(opt => {
          if (opt.requiredPlayers != null) {
            if (!CrazyManager.isPlayerCountAllowedForVariant(opt, newCount, newMode, pCountSelectEl.value)) {
              const chk = document.getElementById(opt.chkId || opt.key);
              if (chk && chk.checked) {
                chk.checked = false;
                const currentSettings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
                if (currentSettings) {
                  currentSettings[opt.key] = false;
                  if (opt.chkId) currentSettings[opt.chkId] = false;
                }
              }
            }
          }
        });
        LobbyController.syncVariantPlayerCountConstraints(context);
      });
    }

    updateCheckAllBonusesState();
  }

  /**
   * Turns off bonus options from all games/brands other than the active brand.
   * Cleanses the target settings object and unchecks corresponding DOM elements.
   * @param {string} activeBrand
   * @param {Object} targetSettings
   * @param {Object} [context]
   * @returns {Object} Cleaned targetSettings
   */
  static disableOtherGameBonusOptions(activeBrand = 'crazy', targetSettings = {}, context = {}) {
    if (!targetSettings) return targetSettings;
    const allBrands = ['crazy', 'clash', 'pints', 'adult'];
    const configs = context.GAME_CONFIGS || (typeof window !== 'undefined' ? window.GAME_CONFIGS : null);

    allBrands.forEach(b => {
      if (b === activeBrand) return;

      // 1. If GAME_CONFIGS schema is available, query and disable all options for brand b
      if (configs && typeof configs.getGame === 'function') {
        try {
          const gameConf = configs.getGame(b);
          if (gameConf && Array.isArray(gameConf.options)) {
            gameConf.options.forEach(opt => {
              if (opt.key) targetSettings[opt.key] = false;
              if (opt.chkId) targetSettings[opt.chkId] = false;
              if (opt.prefix) targetSettings[opt.prefix] = false;
              if (opt.type === 'trackItem') {
                targetSettings[`${opt.key}Count`] = 0;
                targetSettings[`${opt.key}Starts`] = [];
                targetSettings[`${opt.key}Holes`] = [];
                targetSettings[`${opt.key}Dests`] = [];
                targetSettings[`${opt.key}Pegs`] = 0;
              }
              if (opt.type === 'skunkSpecial') {
                targetSettings.optSkunkMarkers = false;
                targetSettings.skunkMarkers = false;
              }
            });
          }
        } catch (e) {}
      }

      // 2. Explicit comprehensive fallbacks for each brand
      if (b === 'clash') {
        const clashKeys = [
          'optNoPassingLanes', 'noPassingLanes',
          'optJump', 'jump',
          'optJail', 'jail',
          'optBar', 'bar',
          'optTimeout', 'timeout',
          'optDetour', 'detour',
          'optRoundAbout', 'roundAbout',
          'optSigns', 'signs',
          'optStopSign', 'stopSign',
          'optDice', 'dice',
          'optWheel', 'wheel',
          'optSpinningWheel', 'spinningWheel',
          'optSkunkMarkers', 'skunkMarkers'
        ];
        clashKeys.forEach(k => { targetSettings[k] = false; });
        targetSettings.noPassingLaneStarts = [];
        targetSettings.noPassingLaneHoles = [];
        targetSettings.noPassingLaneCount = 0;
        targetSettings.noPassingLanesCount = 0;
        targetSettings.jumpStarts = [];
        targetSettings.jumpDests = [];
        targetSettings.jumpCount = 0;
        targetSettings.optJumpCount = 0;
        targetSettings.jailHoles = [];
        targetSettings.jailStarts = [];
        targetSettings.jailCount = 0;
        targetSettings.barHoles = [];
        targetSettings.barStarts = [];
        targetSettings.barCount = 0;
        targetSettings.timeoutHoles = [];
        targetSettings.timeoutStarts = [];
        targetSettings.timeoutCount = 0;
        targetSettings.detourHoles = [];
        targetSettings.detourStarts = [];
        targetSettings.detourCount = 0;
        targetSettings.roundAboutHoles = [];
        targetSettings.roundAboutStarts = [];
        targetSettings.roundAboutCount = 0;
        targetSettings.signsBlocks = [];
        targetSettings.signsCount = 0;
        targetSettings.stopSignHoles = [];
        targetSettings.stopSignStarts = [];
        targetSettings.stopSignCount = 0;
        targetSettings.diceHoles = [];
        targetSettings.diceCount = 0;
        targetSettings.optDiceCount = 0;
        targetSettings.wheelHoles = [];
        targetSettings.wheelCount = 0;
        targetSettings.optWheelCount = 0;
        targetSettings.spinningWheelHoles = [];
        targetSettings.spinningWheelCount = 0;
        targetSettings.clashPreset = null;
        targetSettings.clashCustomLayoutId = null;
      } else if (b === 'crazy') {
        const crazyKeys = [
          'jokers', 'optJokers',
          'optCutBeforeDiscard', 'opt31sFor5', 'opt19sFor2',
          'optRoundAroundTheWorld', 'optMultipleRunsInPegging', 'optAllRed', 'optAllBlack',
          'optAllOdd', 'optAllEven', 'optAllDifferentSuits',
          'optKH', 'optQS', 'optAS',
          'optStraight', 'optFullHouse', 'optStraightFlush', 'optRoyalFlush',
          'optFlushInPlay',
          'optPegThroughZones', 'optLast5PegOut', 'optBunkers',
          'optStinkHole', 'optStolenCrib', 'optZeroHand',
          'optZeroCrib', 'optRockCrib', 'optPickYourCrib', 'optSpeedoCribbage', 'optShotgunCribbage', 'optTwistedCribbage', 'optChutesAndLadders', 'optSpades', 'optGuessStarter', 'optFivesCribbage', 'optSevensAndEightsCribbage', 'optDrawCribbage', 'opt7CardCribbage', 'optProgressiveCut', 'optCanadianDoubles', 'optProgressiveSteals'
        ];
        crazyKeys.forEach(k => { targetSettings[k] = false; });
      } else if (b === 'pints') {
        const pintsKeys = [
          'youDrink', 'optYouDrink',
          'guysDrink', 'optGuysDrink',
          'galsDrink', 'optGalsDrink',
          'leftDrink', 'optLeftDrink',
          'rightDrink', 'optRightDrink',
          'everyoneDrink', 'optEveryoneDrink',
          'everyoneButYouDrink', 'optEveryoneButYouDrink',
          'pickWhoDrinks', 'optPickWhoDrinks'
        ];
        pintsKeys.forEach(k => {
          targetSettings[k] = false;
          targetSettings[`${k}Count`] = 0;
          targetSettings[`${k}Holes`] = [];
          targetSettings[`${k}Pegs`] = 0;
        });
      } else if (b === 'adult') {
        const adultKeys = [
          'loseClothing', 'optLoseClothing',
          'gainClothing', 'optGainClothing',
          'pickWhoLosesClothing', 'optPickWhoLosesClothing',
          'rightLosesClothing', 'optRightLosesClothing',
          'leftLosesClothing', 'optLeftLosesClothing',
          'removeClothingChoice', 'optRemoveClothingChoice',
          'touchBreast', 'optTouchBreast',
          'lickBreast', 'optLickBreast',
          'suckBreast', 'optSuckBreast',
          'touchGenitals', 'optTouchGenitals',
          'lickGenitals', 'optLickGenitals',
          'suckGenitals', 'optSuckGenitals',
          'fingerGenitals', 'optFingerGenitals',
          'genitalIntercourse', 'optGenitalIntercourse',
          'touchAnus', 'optTouchAnus',
          'lickAnus', 'optLickAnus',
          'fingerAnus', 'optFingerAnus',
          'analIntercourse', 'optAnalIntercourse'
        ];
        adultKeys.forEach(k => {
          targetSettings[k] = false;
          targetSettings[`${k}Count`] = 0;
          targetSettings[`${k}Holes`] = [];
          targetSettings[`${k}Pegs`] = 0;
        });
      }

      // 3. Uncheck DOM checkboxes if present
      if (typeof document !== 'undefined') {
        try {
          if (b === 'clash') {
            const clashChkIds = [
              'optNoPassingLanes', 'optJump', 'optJail', 'optBar',
              'optTimeout', 'optDetour', 'optRoundAbout', 'optSigns',
              'optStopSign', 'optDice', 'optWheel', 'optSpinningWheel',
              'optSkunkMarkers'
            ];
            clashChkIds.forEach(id => {
              const el = document.getElementById(id);
              if (el) el.checked = false;
            });
            const clashOnly = document.querySelectorAll('.clash-only-options input[type="checkbox"], #cribbage-clash-options input[type="checkbox"]');
            clashOnly.forEach(el => { el.checked = false; });
          } else if (b === 'crazy') {
            const crazyChkIds = [
              'optJokers', 'optCutBeforeDiscard', 'opt31sFor5', 'opt19sFor2',
              'optRoundAroundTheWorld', 'optMultipleRunsInPegging', 'optAllRed', 'optAllBlack',
              'optAllOdd', 'optAllEven', 'optAllDifferentSuits',
              'optKH', 'optQS', 'optAS', 'optStraight', 'optFullHouse', 'optStraightFlush',
              'optRoyalFlush', 'optFlushInPlay', 'optPegThroughZones', 'optLast5PegOut',
              'optBunkers', 'optStinkHole', 'optStolenCrib', 'optZeroHand',
              'optZeroCrib', 'optRockCrib', 'optPickYourCrib', 'optSpeedoCribbage', 'optShotgunCribbage', 'optTwistedCribbage', 'optChutesAndLadders', 'optSpades', 'optGuessStarter', 'optFivesCribbage', 'optSevensAndEightsCribbage', 'optDrawCribbage', 'opt7CardCribbage', 'optProgressiveCut', 'optCanadianDoubles', 'optProgressiveSteals'
            ];
            crazyChkIds.forEach(id => {
              const el = document.getElementById(id);
              if (el) el.checked = false;
            });
            const checkAll = document.getElementById('checkAllBonuses');
            if (checkAll) checkAll.checked = false;
          } else if (b === 'pints') {
            const pintsChkIds = [
              'optYouDrink', 'optGuysDrink', 'optGalsDrink', 'optLeftDrink',
              'optRightDrink', 'optEveryoneDrink', 'optEveryoneButYouDrink',
              'optPickWhoDrinks'
            ];
            pintsChkIds.forEach(id => {
              const el = document.getElementById(id);
              if (el) el.checked = false;
            });
            const pintsOnly = document.querySelectorAll('#pints-options input[type="checkbox"], .pints-only-options input[type="checkbox"]');
            pintsOnly.forEach(el => { el.checked = false; });
          } else if (b === 'adult') {
            const adultChkIds = [
              'optLoseClothing', 'optGainClothing', 'optPickWhoLosesClothing',
              'optRightLosesClothing', 'optLeftLosesClothing', 'optRemoveClothingChoice',
              'optTouchBreast', 'optLickBreast', 'optSuckBreast', 'optTouchGenitals',
              'optLickGenitals', 'optSuckGenitals', 'optFingerGenitals',
              'optGenitalIntercourse', 'optTouchAnus', 'optLickAnus',
              'optFingerAnus', 'optAnalIntercourse'
            ];
            adultChkIds.forEach(id => {
              const el = document.getElementById(id);
              if (el) el.checked = false;
            });
            const adultOnly = document.querySelectorAll('#adult-options input[type="checkbox"], .adult-only-options input[type="checkbox"]');
            adultOnly.forEach(el => { el.checked = false; });
          }
        } catch (e) {}
      }
    });

    if (activeBrand === 'pints') {
      targetSettings.brand = 'pints';
      if (targetSettings.target && [241, 361, 481].includes(Number(targetSettings.target))) {
        targetSettings.target = 121;
      }
      if (targetSettings.selected3DBoardId === 'board-cribbage-clash' || targetSettings.selected3DBoardId === 'board-cribbage-clash-square') {
        targetSettings.selected3DBoardId = 'board-14';
      }
    }

    return targetSettings;
  }

  /**
   * Reads lobby form controls and returns a normalized settings object.
   * @param {Object} [context]
   * @returns {Object}
   */
  static readFormSettings(context = {}) {
    if (typeof document === 'undefined') return {};
    const getAppBrand = context.getAppBrand || (typeof window !== 'undefined' ? window.getAppBrand : () => 'crazy');
    const brand = getAppBrand();
    const GameOptionsEngine = context.GameOptionsEngine || (typeof window !== 'undefined' ? window.GameOptionsEngine : null);
    const allowCpu = GameOptionsEngine && typeof GameOptionsEngine.isCpuAllowed === 'function'
      ? GameOptionsEngine.isCpuAllowed(brand)
      : !['pints', 'adult'].includes(brand);
    const parseGameMode = context.parseGameMode || (typeof window !== 'undefined' ? window.parseGameMode : (v => ({ playerCount: parseInt(v, 10) || 2, gameMode: 'individual' })));
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});

    const pCountVal = document.getElementById('optPlayerCount') ? document.getElementById('optPlayerCount').value : '2';
    const { playerCount: pNum, gameMode: gMode } = parseGameMode(pCountVal);

    const getSeatVal = (id, fallback) => {
      const el = document.getElementById(id);
      const val = el ? el.value : fallback;
      if (val === 'human') return 'online';
      return (!allowCpu && val.startsWith('cpu:')) ? 'online' : val;
    };
    const rawOpp = document.getElementById('optOpp') ? document.getElementById('optOpp').value : 'cpu';
    let opp = (!allowCpu && rawOpp === 'cpu') ? 'online' : rawOpp;
    if (opp === 'human') opp = allowCpu ? 'cpu' : 'online';

    const res = {
      playerCount: pNum,
      gameMode: gMode || 'individual',
      seat2Type: getSeatVal('optSeat2Type', 'cpu:shark'),
      seat3Type: getSeatVal('optSeat3Type', 'cpu:regular'),
      seat4Type: getSeatVal('optSeat4Type', 'cpu:turtler'),
      seat5Type: getSeatVal('optSeat5Type', 'cpu:maverick'),
      seat6Type: getSeatVal('optSeat6Type', 'cpu:master'),
      seat7Type: getSeatVal('optSeat7Type', 'cpu:rookie'),
      seat8Type: getSeatVal('optSeat8Type', 'cpu:casual'),
      opp: opp,
      aiProfile: document.getElementById('optAiProfile') ? document.getElementById('optAiProfile').value : 'shark',
      coach: document.getElementById('optCoach') ? document.getElementById('optCoach').value : 'off',
      deal: document.getElementById('optDeal') ? document.getElementById('optDeal').value : 'std',
      dealStyle: document.getElementById('optDealStyle') ? document.getElementById('optDealStyle').value : 'alternate',
      showDetail: document.getElementById('optShowDetail') ? document.getElementById('optShowDetail').value : 'highlight',
      cardAssassin: (document.getElementById('optCardAssassin') && document.getElementById('optCardAssassin').value) || (settings.cardAssassin || 'no'),
      jailAnimation: (document.getElementById('optJailAnimation') && document.getElementById('optJailAnimation').value) || (settings.jailAnimation || 'yes'),
      barAnimation: (document.getElementById('optBarAnimation') && document.getElementById('optBarAnimation').value) || (settings.barAnimation || 'yes'),
      cannonAnimation: (document.getElementById('optCannonAnimation') && document.getElementById('optCannonAnimation').value) || (settings.cannonAnimation || 'yes'),
      jumpAnimation: (document.getElementById('optCannonAnimation') && document.getElementById('optCannonAnimation').value) || (settings.jumpAnimation || settings.cannonAnimation || 'yes'),
      speedTrapAnimation: (document.getElementById('optSpeedTrapAnimation') && document.getElementById('optSpeedTrapAnimation').value) || (settings.speedTrapAnimation || 'yes'),
      stopSignAnimation: (document.getElementById('optSpeedTrapAnimation') && document.getElementById('optSpeedTrapAnimation').value) || (settings.stopSignAnimation || settings.speedTrapAnimation || 'yes'),
      stopAnimation: (document.getElementById('optSpeedTrapAnimation') && document.getElementById('optSpeedTrapAnimation').value) || (settings.stopAnimation || settings.speedTrapAnimation || 'yes'),
      detourAnimation: (document.getElementById('optDetourAnimation') && document.getElementById('optDetourAnimation').value) || (settings.detourAnimation || settings.scenicRouteAnimation || 'yes'),
      roundAboutAnimation: (document.getElementById('optRoundAboutAnimation') && document.getElementById('optRoundAboutAnimation').value) || (settings.roundAboutAnimation || settings.scenicRouteAnimation || 'yes'),
      scenicRouteAnimation: (document.getElementById('optScenicRouteAnimation') && document.getElementById('optScenicRouteAnimation').value) || (document.getElementById('optDetourAnimation') && document.getElementById('optDetourAnimation').value) || (settings.scenicRouteAnimation || 'yes'),
      goal: (brand === 'clash') ? 'high' : (document.getElementById('optGoal') ? document.getElementById('optGoal').value : 'high'),
      target: (() => {
        const rawTarget = document.getElementById('optTarget') ? parseInt(document.getElementById('optTarget').value, 10) : (settings.target || 121);
        return (brand === 'clash' && ![241, 361, 481].includes(rawTarget)) ? 481 : rawTarget;
      })(),
      decks: document.getElementById('optDecks') ? (parseInt(document.getElementById('optDecks').value, 10) || 1) : 1,
      cardBack: (document.getElementById('optCardBack') && document.getElementById('optCardBack').value) || (settings.cardBack || 'classic-blue'),
      boardFinish: (document.getElementById('optBoardFinish') && document.getElementById('optBoardFinish').value) || (settings.boardFinish || 'maple'),
      pegSkin: (document.getElementById('optPegSkin') && document.getElementById('optPegSkin').value) || (settings.pegSkin || 'brass'),
      pegStyle: (document.getElementById('optPegStyle') && document.getElementById('optPegStyle').value) || (settings.pegStyle || 'classic'),
      tableEnvironment: (document.getElementById('optTableEnvironment') && document.getElementById('optTableEnvironment').value) || (settings.tableEnvironment || 'auto'),
      optActionCam: (document.getElementById('optActionCam') && document.getElementById('optActionCam').value) || (settings.optActionCam || 'no'),
      seriesMode: (document.getElementById('optSeriesMode') && document.getElementById('optSeriesMode').value) || (settings.seriesMode || 'bo3'),
      boardViewMode: (document.getElementById('optBoardViewModal') && document.getElementById('optBoardViewModal').value) || (document.getElementById('optBoardView') && document.getElementById('optBoardView').value) || (settings.boardViewMode || 'room3d-fullscreen'),
      selected3DBoardId: (document.getElementById('opt3DBoard') && document.getElementById('opt3DBoard').value) || (settings.selected3DBoardId || 'board-14')
    };

    if (GameOptionsEngine && typeof GameOptionsEngine.readBrandSettings === 'function') {
      Object.assign(res, GameOptionsEngine.readBrandSettings(brand));
    }

    // Synchronize variant player constraints in readFormSettings
    const activeRestricted = CRAZY_OPTIONS_DEFS.find(opt => {
      if (opt.requiredPlayers == null) return false;
      const chk = document.getElementById(opt.chkId || opt.key);
      return chk ? chk.checked : Boolean(res[opt.key] || res[opt.chkId]);
    });
    if (activeRestricted) {
      res[activeRestricted.key] = true;
      const req = CrazyManager.getVariantRequiredPlayers(activeRestricted);
      if (req && !req.includes(Number(res.playerCount))) {
        res.playerCount = req[0];
      }
      if (activeRestricted.requiredGameMode) {
        res.gameMode = activeRestricted.requiredGameMode;
      }
    }
    CrazySettings.sanitizeCrazyOptions(res);

    const sanitizeClash = context.sanitizeClashBlockPlacements || (typeof window !== 'undefined' ? window.sanitizeClashBlockPlacements : null);
    if (typeof sanitizeClash === 'function') {
      sanitizeClash(res);
    }
    LobbyController.disableOtherGameBonusOptions(brand, res, context);
    return res;
  }

  /**
   * Updates state of master 'Check All Bonuses' checkbox based on children checkboxes.
   */
  static updateCheckAllBonusesState() {
    if (typeof document === 'undefined') return;
    const checkAll = document.getElementById('checkAllBonuses');
    if (!checkAll) return;
    const opts = document.querySelectorAll('.bonuses .chk input');
    if (!opts.length) return;
    checkAll.checked = Array.from(opts).every(opt => opt.checked);
    const GameOptionsEngine = (typeof window !== 'undefined' ? window.GameOptionsEngine : null) || (typeof globalThis !== 'undefined' ? globalThis.GameOptionsEngine : null);
    if (GameOptionsEngine && typeof GameOptionsEngine.updateSubgroupActiveBadges === 'function') {
      GameOptionsEngine.updateSubgroupActiveBadges('crazy', document.getElementById('brand-game-options'));
    }
  }

  /**
   * Starts an online multiplayer match as the host.
   * @param {Object} [context]
   */
  static startGameOnline(context = {}) {
    if (typeof document === 'undefined') return;
    const getAppBrand = context.getAppBrand || (typeof window !== 'undefined' ? window.getAppBrand : () => 'crazy');
    const isAgeVerified = context.isAgeVerified || (typeof window !== 'undefined' && window.isAgeVerified ? window.isAgeVerified : ((b) => (b !== 'pints' && b !== 'adult')));
    const showAgeGateModal = context.showAgeGateModal || (typeof window !== 'undefined' ? window.showAgeGateModal : () => {});
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const net = context.net || (typeof window !== 'undefined' ? window.net : {});
    const hideModal = context.hideModal || (typeof window !== 'undefined' ? window.hideModal : () => {});
    const startGame = context.startGame || (typeof window !== 'undefined' ? window.startGame : LobbyController.startGame);
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});

    const currentBrand = getAppBrand();
    if ((currentBrand === 'pints' || currentBrand === 'adult') && !isAgeVerified(currentBrand)) {
      showAgeGateModal(currentBrand);
      return;
    }
    const optOppEl = document.getElementById('optOpp');
    if (optOppEl) optOppEl.value = 'online';
    settings.opp = 'online';
    settings.netRole = 'host';
    net.isHost = true;
    net.mySeatId = 'p1';
    net.ready = true;
    hideModal();
    startGame(context);
    netPush({ fancy: { text: 'Game Started!', kind: 'famous', ms: 1600 } });
  }

  /**
   * Initiates gameplay from the lobby form settings.
   * @param {Object} [context]
   */
  static startGame(context = {}) {
    if (typeof document === 'undefined') return;
    const getAppBrand = context.getAppBrand || (typeof window !== 'undefined' ? window.getAppBrand : () => 'crazy');
    const isAgeVerified = context.isAgeVerified || (typeof window !== 'undefined' && window.isAgeVerified ? window.isAgeVerified : ((b) => (b !== 'pints' && b !== 'adult')));
    const showAgeGateModal = context.showAgeGateModal || (typeof window !== 'undefined' ? window.showAgeGateModal : () => {});
    const hideMenuOptions = context.hideMenuOptions || (typeof window !== 'undefined' ? (window.hideMenuOptions || window.hideMenuOtions) : () => {});
    const parseGameMode = context.parseGameMode || (typeof window !== 'undefined' ? window.parseGameMode : (v => ({ playerCount: parseInt(v, 10) || 2, gameMode: 'individual' })));
    const GameOptionsEngine = context.GameOptionsEngine || (typeof window !== 'undefined' ? window.GameOptionsEngine : null);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const net = context.net || (typeof window !== 'undefined' ? window.net : {});
    const hostRoom = context.hostRoom || (typeof window !== 'undefined' ? window.hostRoom : () => {});
    const setCardBack = context.setCardBack || (typeof window !== 'undefined' ? window.setCardBack : () => {});
    const readFormSettings = context.readFormSettings || (typeof window !== 'undefined' ? window.readFormSettings : LobbyController.readFormSettings);
    const sanitizeClashBlockPlacements = context.sanitizeClashBlockPlacements || (typeof window !== 'undefined' ? window.sanitizeClashBlockPlacements : null);
    const saveSettingsStore = context.saveSettingsStore || (typeof window !== 'undefined' ? window.saveSettingsStore : () => {});
    const makeDeck = context.makeDeck || (typeof window !== 'undefined' ? window.makeDeck : null);
    const applySeatLabels = context.applySeatLabels || (typeof window !== 'undefined' ? window.applySeatLabels : () => {});
    const resetMatch = context.resetMatch || (typeof window !== 'undefined' ? window.resetMatch : () => {});

    const currentBrand = getAppBrand();
    if ((currentBrand === 'pints' || currentBrand === 'adult') && !isAgeVerified(currentBrand)) {
      showAgeGateModal(currentBrand);
      return;
    }
    hideMenuOptions();
    if (typeof window !== 'undefined' && typeof window.hideStartGameModal === 'function') {
      window.hideStartGameModal();
    }
    const startOverlay = document.getElementById('startGameOverlay');
    if (startOverlay) startOverlay.style.display = 'none';

    if (typeof window !== 'undefined' && window.scrollTo) {
      window.scrollTo(0, 0);
    }

    const isTourney = Boolean(context.isTournamentMatch || (context.settings && context.settings.isTournamentMatch));
    settings.isTournamentMatch = isTourney;
    if (typeof window !== 'undefined' && window.settings) {
      window.settings.isTournamentMatch = isTourney;
    }

    if (!settings.isTournamentMatch) {
      const activeRestricted = CRAZY_OPTIONS_DEFS.find(opt => {
        if (opt.requiredPlayers == null) return false;
        const chk = document.getElementById(opt.chkId || opt.key);
        return chk ? chk.checked : Boolean(settings[opt.key] || settings[opt.chkId]);
      });
      if (activeRestricted) {
        settings[activeRestricted.key] = true;
        const req = CrazyManager.getVariantRequiredPlayers(activeRestricted);
        if (req && !req.includes(Number(settings.playerCount))) {
          settings.playerCount = req[0];
        }
        if (activeRestricted.requiredGameMode) {
          settings.gameMode = activeRestricted.requiredGameMode;
        }
      } else {
        const pCountVal = (document.getElementById('optPlayerCount') && document.getElementById('optPlayerCount').value) || '2';
        const parsedMode = parseGameMode(pCountVal);
        settings.playerCount = parsedMode.playerCount;
        settings.gameMode = parsedMode.gameMode;
      }

      const brand = getAppBrand();
      const allowCpu = GameOptionsEngine && typeof GameOptionsEngine.isCpuAllowed === 'function'
        ? GameOptionsEngine.isCpuAllowed(brand)
        : !['pints', 'adult'].includes(brand);

      const rawOpp = (document.getElementById('optOpp') && document.getElementById('optOpp').value) || (allowCpu ? 'cpu' : 'online');
      let opp = (!allowCpu && rawOpp === 'cpu') ? 'online' : rawOpp;
      if (opp === 'human') opp = allowCpu ? 'cpu' : 'online';
      settings.opp = opp;
      if (settings.opp === 'online' && !net.peer) {
        hostRoom();
        return;
      }

      settings.aiProfile = (document.getElementById('optAiProfile') && document.getElementById('optAiProfile').value) || 'shark';
    } else {
      settings.playerCount = 2;
      settings.gameMode = 'individual';
    }

    const brand = getAppBrand();
    const allowCpu = GameOptionsEngine && typeof GameOptionsEngine.isCpuAllowed === 'function'
      ? GameOptionsEngine.isCpuAllowed(brand)
      : !['pints', 'adult'].includes(brand);

    settings.coach = (document.getElementById('optCoach') && document.getElementById('optCoach').value) || 'off';
    settings.deal = (settings.playerCount >= 3) ? 'std' : ((document.getElementById('optDeal') && document.getElementById('optDeal').value) || 'std');
    settings.dealStyle = (document.getElementById('optDealStyle') && document.getElementById('optDealStyle').value) || 'alternate';
    settings.showDetail = (document.getElementById('optShowDetail') && document.getElementById('optShowDetail').value) || 'highlight';
    settings.cardAssassin = (document.getElementById('optCardAssassin') && document.getElementById('optCardAssassin').value) || (settings.cardAssassin || 'no');
    settings.jailAnimation = (document.getElementById('optJailAnimation') && document.getElementById('optJailAnimation').value) || (settings.jailAnimation || 'yes');
    settings.barAnimation = (document.getElementById('optBarAnimation') && document.getElementById('optBarAnimation').value) || (settings.barAnimation || 'yes');
    settings.cannonAnimation = (document.getElementById('optCannonAnimation') && document.getElementById('optCannonAnimation').value) || (settings.cannonAnimation || 'yes');
    settings.jumpAnimation = settings.cannonAnimation;
    settings.speedTrapAnimation = (document.getElementById('optSpeedTrapAnimation') && document.getElementById('optSpeedTrapAnimation').value) || (settings.speedTrapAnimation || 'yes');
    settings.stopSignAnimation = settings.speedTrapAnimation;
    settings.stopAnimation = settings.speedTrapAnimation;
    settings.detourAnimation = (document.getElementById('optDetourAnimation') && document.getElementById('optDetourAnimation').value) || (settings.detourAnimation || settings.scenicRouteAnimation || 'yes');
    settings.roundAboutAnimation = (document.getElementById('optRoundAboutAnimation') && document.getElementById('optRoundAboutAnimation').value) || (settings.roundAboutAnimation || settings.scenicRouteAnimation || 'yes');
    settings.scenicRouteAnimation = (document.getElementById('optScenicRouteAnimation') && document.getElementById('optScenicRouteAnimation').value) || settings.detourAnimation || (settings.scenicRouteAnimation || 'yes');
    if (settings.showDetail === 'muggins') {
      settings.optMuggins = true;
    }
    settings.goal = (brand === 'clash') ? 'high' : ((document.getElementById('optGoal') && document.getElementById('optGoal').value) || 'high');
    let targetVal = document.getElementById('optTarget') ? parseInt(document.getElementById('optTarget').value, 10) : (settings.target || 121);
    if (brand === 'clash' && ![241, 361, 481].includes(targetVal)) {
      targetVal = 481;
      const optTargetEl = document.getElementById('optTarget');
      if (optTargetEl) optTargetEl.value = '481';
    }
    settings.target = targetVal;
    settings.decks = document.getElementById('optDecks') ? (parseInt(document.getElementById('optDecks').value, 10) || 1) : 1;
    settings.cardBack = (document.getElementById('optCardBack') && document.getElementById('optCardBack').value) || (settings.cardBack || 'classic-blue');
    setCardBack(settings.cardBack, true);
    settings.boardFinish = (document.getElementById('optBoardFinish') && document.getElementById('optBoardFinish').value) || (settings.boardFinish || 'maple');
    settings.pegSkin = (document.getElementById('optPegSkin') && document.getElementById('optPegSkin').value) || (settings.pegSkin || 'brass');
    settings.optActionCam = (document.getElementById('optActionCam') && document.getElementById('optActionCam').value) || (settings.optActionCam || 'no');
    settings.seriesMode = (document.getElementById('optSeriesMode') && document.getElementById('optSeriesMode').value) || (settings.seriesMode || 'bo3');
    SeriesTracker.setMode(settings.seriesMode);
    settings.boardViewMode = (document.getElementById('optBoardViewModal') && document.getElementById('optBoardViewModal').value) || (document.getElementById('optBoardView') && document.getElementById('optBoardView').value) || (settings.boardViewMode || 'room3d-fullscreen');
    settings.selected3DBoardId = (document.getElementById('opt3DBoard') && document.getElementById('opt3DBoard').value) || (settings.selected3DBoardId || 'board-14');

    if (!settings.isTournamentMatch && typeof LobbyController.resolveSeatConfigs === 'function') {
      settings.seats = LobbyController.resolveSeatConfigs(settings.playerCount, allowCpu, {
        opp: settings.opp,
        aiProfile: settings.aiProfile
      });
    }

    if (settings.opp === 'online') {
      const hostName = (document.getElementById('optPlayerName') && document.getElementById('optPlayerName').value.trim()) || 'Host';
      if (settings.seats && settings.seats[0]) {
        settings.seats[0].name = hostName;
        settings.seats[0].type = 'human';
        settings.seats[0].online = true;
      }
      if (net.clients) {
        for (const client of net.clients.values()) {
          if (client.seatId) {
            const s = settings.seats.find(x => x.id === client.seatId);
            if (s) {
              s.name = client.name || `Player ${client.seatId.slice(1)}`;
              s.type = 'human';
              s.online = Boolean(client.conn && client.conn.open);
            }
          }
        }
      }
    }

    settings.brand = currentBrand;
    LobbyController.disableOtherGameBonusOptions(currentBrand, settings, context);
    if (typeof window !== 'undefined' && window.settings) {
      window.settings.brand = currentBrand;
      LobbyController.disableOtherGameBonusOptions(currentBrand, window.settings, context);
    }

    const formSettings = readFormSettings(context);
    LobbyController.disableOtherGameBonusOptions(currentBrand, formSettings, context);
    Object.assign(settings, formSettings);
    const chosenBoardViewMode = (document.getElementById('optBoardViewModal') && document.getElementById('optBoardViewModal').value) || (document.getElementById('optBoardView') && document.getElementById('optBoardView').value) || (settings.boardViewMode || 'room3d-fullscreen');
    settings.boardViewMode = chosenBoardViewMode;
    if (typeof window !== 'undefined' && window.settings) {
      window.settings.boardViewMode = chosenBoardViewMode;
    }
    LobbyController.disableOtherGameBonusOptions(currentBrand, settings, context);
    if (typeof window !== 'undefined' && window.settings) {
      LobbyController.disableOtherGameBonusOptions(currentBrand, window.settings, context);
    }

    let optPegThroughZones = settings.optPegThroughZones || false;
    let optLast5PegOut = settings.optLast5PegOut || false;
    let optBunkers = settings.optBunkers || false;
    let optStinkHole = settings.optStinkHole || false;
    if (optBunkers) {
      optPegThroughZones = false;
      optLast5PegOut = false;
      optStinkHole = false;
    } else if (optLast5PegOut) {
      optPegThroughZones = false;
      optStinkHole = false;
    } else if (optPegThroughZones) {
      optStinkHole = false;
    }
    settings.optPegThroughZones = optPegThroughZones;
    settings.optLast5PegOut = optLast5PegOut;
    settings.optBunkers = optBunkers;
    settings.optStinkHole = optStinkHole;

    if (typeof sanitizeClashBlockPlacements === 'function') {
      sanitizeClashBlockPlacements(settings);
    }
    saveSettingsStore(formSettings);
    if (typeof makeDeck === 'function') {
      if (typeof window !== 'undefined') {
        window.FULL_DECK = makeDeck(settings.decks);
      }
    }

    const menuEl = document.getElementById('menu');
    const gameEl = document.getElementById('game');
    if (menuEl) menuEl.classList.add('hidden');
    if (gameEl) gameEl.classList.remove('hidden');

    if (settings.boardViewMode === 'room3d' || settings.boardViewMode === '3d' || settings.boardViewMode === 'room3d-fullscreen') {
      try {
        const loaderCls = (typeof Room3DLoadingOverlay !== 'undefined') ? Room3DLoadingOverlay : (typeof window !== 'undefined' ? window.Room3DLoadingOverlay : null);
        if (loaderCls && typeof loaderCls.show === 'function') {
          loaderCls.show(document.body, {
            settings,
            is3DOnly: settings.boardViewMode === '3d',
            isGlobal: true,
            cardBack: settings?.cardBack,
            theme: settings?.roomTheme || settings?.tableEnvironment,
            deck: (typeof window !== 'undefined' ? window.FULL_DECK : null),
            minDuration: 1600
          });
        }
      } catch (err) {
        console.warn('[LobbyController] Could not display Room3DLoadingOverlay:', err);
      }
    }

    ReactionManager.injectReactionUi();
    SeriesTracker.updateSeriesUi();

    const appBrand = brand;
    const gameTitleEl = document.getElementById('gameHeaderTitle') || document.querySelector('.header-title-wrap h1');
    if (gameTitleEl) {
      gameTitleEl.textContent = (appBrand === 'clash') ? 'CRIBBAGE CLASH' : ((appBrand === 'pints') ? 'PINTS AND PEGS' : ((appBrand === 'adult') ? 'SKUNKED AND STRIPPED' : 'CRAZY CRIBBAGE'));
    }
    applySeatLabels();

    let pills = [];
    if (typeof LobbyController.generateModePills === 'function') {
      pills = LobbyController.generateModePills(settings, {
        brand: currentBrand,
        getAppBrand: () => currentBrand,
        allowCpu,
        isOnline: context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false),
        isHvH: context.isHvH || (typeof window !== 'undefined' ? window.isHvH : () => false),
        is3Player1vs2: context.is3Player1vs2 || (typeof window !== 'undefined' ? window.is3Player1vs2 : () => false),
        getNoPassingLanes: context.getNoPassingLanes || (typeof window !== 'undefined' ? window.getNoPassingLanes : () => []),
        getJumpConfigs: context.getJumpConfigs || (typeof window !== 'undefined' ? window.getJumpConfigs : () => []),
        getJailConfigs: context.getJailConfigs || (typeof window !== 'undefined' ? window.getJailConfigs : () => []),
        getBarConfigs: context.getBarConfigs || (typeof window !== 'undefined' ? window.getBarConfigs : () => []),
        getTimeoutConfigs: context.getTimeoutConfigs || (typeof window !== 'undefined' ? window.getTimeoutConfigs : () => []),
        getDetourConfigs: context.getDetourConfigs || (typeof window !== 'undefined' ? window.getDetourConfigs : () => []),
        getRoundAboutConfigs: context.getRoundAboutConfigs || (typeof window !== 'undefined' ? window.getRoundAboutConfigs : () => []),
        getSignConfigs: context.getSignConfigs || (typeof window !== 'undefined' ? window.getSignConfigs : () => []),
        getDiceConfigs: context.getDiceConfigs || (typeof window !== 'undefined' ? window.getDiceConfigs : () => []),
        getWheelConfigs: context.getWheelConfigs || (typeof window !== 'undefined' ? window.getWheelConfigs : () => [])
      });
    }

    const modePillsEl = document.getElementById('modePills');
    if (modePillsEl) {
      modePillsEl.innerHTML = pills.map(p => `<span class="pill">${p}</span>`).join('');
    }

    ['introCopy', 'introCopyClash', 'introCopyAdult', 'introCopyPints'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.hidden = true;
    });

    resetMatch();

    if (settings.boardViewMode === 'room3d-fullscreen') {
      try {
        const b3dWrap = document.getElementById('board3DContainer');
        if (b3dWrap) {
          if (b3dWrap.requestFullscreen) {
            b3dWrap.requestFullscreen().catch(() => {});
          } else if (b3dWrap.webkitRequestFullscreen) {
            b3dWrap.webkitRequestFullscreen();
          }
        }
      } catch (_) {}
    }
  }

  startGameOnline(context) {
    return LobbyController.startGameOnline(context);
  }

  startGame(context) {
    return LobbyController.startGame(context);
  }

  readFormSettings(context) {
    return LobbyController.readFormSettings(context);
  }

  updateCheckAllBonusesState() {
    return LobbyController.updateCheckAllBonusesState();
  }

  randomizeCrazyCribbageBonuses(context) {
    return LobbyController.randomizeCrazyCribbageBonuses(context);
  }

  checkInitialAgeGate(context) {
    return LobbyController.checkInitialAgeGate(context);
  }

  setupCheckAllBonuses(context) {
    return LobbyController.setupCheckAllBonuses(context);
  }

  disableOtherGameBonusOptions(activeBrand, targetSettings, context) {
    return LobbyController.disableOtherGameBonusOptions(activeBrand, targetSettings, context);
  }
}
